import React, { useState, useEffect, useRef, createContext, useContext, useMemo, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { getRoleDashboardPath } from './utils/roleDashboard';
import axios from "axios";
import { dedupedGet } from "./lib/requestDedupe";
import { format, isValid, parseISO } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import ReactSelect from 'react-select';
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { useResizableColumns } from "./hooks/useResizableColumns";
import { ResizableTableHead, ColumnVisibilityMenu, ResizeGuide } from "./components/ResizableTable";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

// ─── IDLE-TOAST-SUPPRESS-001 (Jun 2026) ────────────────────────────────
// While an idle-timeout 401 is being handled by the "Are you still there?"
// warning modal, generic `toast.error(...)` calls (e.g. "Error fetching
// orders") must be suppressed app-wide — the user should only see the
// session warning. Genuine errors outside the idle window are unaffected.
// Flags: `__TF_IDLE_MODAL_OPEN` (IdleManager modal visible) and
// `__TF_IDLE401_LAST` (ms timestamp of the last idle-classified 401,
// cleared on successful Stay-Signed-In). HMR-safe via the __tfWrapped tag.
if (!toast.error.__tfWrapped) {
  const __origToastError = toast.error;
  const wrappedToastError = (message, opts) => {
    const idle401Recent =
      typeof window.__TF_IDLE401_LAST === 'number' &&
      Date.now() - window.__TF_IDLE401_LAST < 15000;
    if (window.__TF_IDLE_MODAL_OPEN === true || idle401Recent) {
      console.warn('[IdleToastSuppress] Suppressed error toast during idle-timeout handling:', message);
      return undefined;
    }
    return __origToastError(message, opts);
  };
  wrappedToastError.__tfWrapped = true;
  toast.error = wrappedToastError;
}
import { Separator } from "./components/ui/separator";
import { Calendar } from "./components/ui/calendar";
import { DatePickerInput } from "./components/ui/date-picker-input";
import IdleManager from "./components/IdleManager";
import GlobalNotificationBanner from "./components/GlobalNotificationBanner";
import AnnouncementsBanner from "./components/shared/AnnouncementsBanner";
import ShiftChangeAcknowledgeModal from "./components/shared/ShiftChangeAcknowledgeModal";
import ForcePasswordChangeGate from "./components/auth/ForcePasswordChangeGate";
import AssignOrdersModal from "./components/AssignOrdersModal";
import { useAutoSave } from "./hooks/useAutoSave";
import { useSessionMonitor } from "./hooks/useSessionMonitor";
import SessionTimeoutModal from "./components/SessionTimeoutModal";
import ErrorBoundary from "./components/ErrorBoundary";
import { isBrowser, safeLocalStorage } from "./utils/browserGuards";
import { useRoleChangeWatcher } from "./hooks/useRoleChangeWatcher";
import { featureManager, initializeFeatureChecks, FEATURE_FLAGS } from "./utils/featureFlags";
import OrderSummaryPopup from "./components/OrderSummaryPopup";
import MailAwayModal from "./components/MailAwayModal";
// TaxCallbackSheet removed - feature abandoned
import { calculateDaysLate, getDaysLateLabel } from "./utils/daysLateCalculator";
import { findJurisdictionByCounty, getSuggestionBannerState } from "./utils/jurisdictionSuggestion";
import JurisdictionSuggestionBanner from "./components/JurisdictionSuggestionBanner";

// Notification Bell (shared)

// Extracted utility imports (imported for future use during incremental refactoring)
import { 
  ACCOUNT_TZ as IMPORTED_ACCOUNT_TZ
} from "./utils/formatters";
import { 
  isProcessorRole as importedIsProcessorRole,
  isTaxProduct
} from "./utils/orderHelpers";
import { showGlassmorphismWelcome, showGlassmorphismLogoutNotice } from "./utils/glassmorphismToasts";
import { queueLogoutNotice, readLogoutNotice, clearLogoutNotice, markLogoutNoticeShown, resetLogoutNoticeState } from "./utils/logoutNotice";
import ProcessorKPICards from "./components/processor/ProcessorKPICards";
import TitleFlowLogo from "./components/TitleFlowLogo";
import { LoginNetworkBackdrop } from "./components/LoginNetworkBackdrop";

// Extracted modals - File management
import { SendFileModal, RenameFileModal, DeleteFileModal } from "./components/modals/FileModals";

// Extracted order components
import { OrderActions, ProcessorActions } from "./components/orders/OrderActions";
import { ClientProductSelector } from "./components/orders/ClientProductSelector";
import FinalizeOrderCard from "./components/orders/FinalizeOrderCard";
import ResendTaxCertificateButton from "./components/orders/ResendTaxCertificateButton";
import OrderReassignmentDialog from "./components/orders/OrderReassignmentDialog";
import OrderBillingPanel from "./components/orders/OrderBillingPanel";

// Extracted order detail components
import { ManualLinkingBanner, OverviewTab, SubmissionsTab, FulfillmentTab, ActivityTab, SummaryTab } from "./components/orders/details";

// Extracted tax certificate components
import { TaxCertificatePanel, ReceivedStatusToggles, ExemptionPillSelector, PrefillIndicator } from "./components/tax";

// Extracted form components
import { CreateCustomerForm } from "./components/forms/CreateCustomerForm";
import { CreateOrderForm } from "./components/forms/CreateOrderForm";
import { ContractCodeChip } from "./components/orders/ProductWithCode";

// Extracted admin components
import { FavoritesManagement } from "./components/admin/FavoritesManagement";
import { ClientCodeManagement } from "./components/admin/ClientCodeManagement";
import ClientPricingManager from "./components/admin/ClientPricingManager";
import ClientSellingPricesPage from "./pages/admin/ClientSellingPricesPage";
import PricingDiagnosticsPage from "./pages/admin/PricingDiagnosticsPage";
import BillingDashboard from "./components/admin/BillingDashboard";
import BulkPaymentUpload from "./pages/BulkPaymentUpload";
import ContractMappingAdmin from "./components/admin/ContractMappingAdmin";
import ProductManagement from "./components/admin/ProductManagement";
import OnlineCoveragePage from "./pages/admin/OnlineCoveragePage";
import GroundRateCardsPage from "./pages/admin/GroundRateCardsPage";
import ImportHistoryPage from "./pages/admin/ImportHistoryPage";

// Extracted reporting components
import ReportingDashboard from "./components/reporting/ReportingDashboard";

// Extracted files components
import { FilesTab } from "./components/files";

// Page Components
import ProcessorOrders from "./pages/ProcessorOrders";
import OrdersManagement from "./pages/OrdersManagement";

// Admin UI imports (PR A6)
import AdminLayout from "./components/AdminLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminRoles from "./pages/AdminRoles";
import AdminAuditLogs from "./pages/AdminAuditLogs";
import AdminEmailSettings from "./pages/AdminEmailSettings";
import AdminBillingSettings from "./pages/AdminBillingSettings";
import AdminSessionSettings from "./pages/AdminSessionSettings";
import AdminQCSettings from "./pages/AdminQCSettings";
import AdminQuickChipsSettings from "./pages/AdminQuickChipsSettings";
// Phase 3 UI — Tax Collectors infra (May 29, 2026)
import TaxCollectorsPage from "./pages/TaxCollectorsPage";
import TaxCategoriesPage from "./pages/TaxCategoriesPage";
import CollectorCorrectionRequestsPage from "./pages/CollectorCorrectionRequestsPage";
import LegacyFallbackQueuePage from "./pages/LegacyFallbackQueuePage";
import AdminTeamManagement from "./components/admin/AdminTeamManagement";
import AutoAssignmentDashboard from "./components/admin/AutoAssignmentDashboard";
import JurisdictionDirectory from "./pages/JurisdictionDirectory";
import JurisdictionHealthReport from "./pages/JurisdictionHealthReport";
import JurisdictionManagerDashboard from "./pages/JurisdictionManagerDashboard";
import JurisdictionRequestsPage from "./pages/JurisdictionRequestsPage";
import JurisdictionRequestorDashboard from "./pages/JurisdictionRequestorDashboard";
import AttendanceReports from "./components/admin/AttendanceReports";
import HREmployeeDirectory from "./components/admin/hr/HREmployeeDirectory";
import EmployeeWorkspace from "./components/admin/hr/EmployeeWorkspace";
import MyProbationReviews from "./components/admin/hr/probation/MyProbationReviews";
import EmployeeProfile from "./components/admin/hr/EmployeeProfile";
import HRAuditLog from "./components/admin/hr/HRAuditLog";
import CelebrationSettings from "./components/hr/CelebrationSettings";
import CelebrationOverlay from "./components/celebrations/CelebrationOverlay";
import BirthdayWishesPanel from "./components/celebrations/BirthdayWishesPanel";
// PR 1 (Phase B1) — Break Management admin pages
import BreakHistoryPage from "./pages/hr/BreakHistoryPage";
import MyQualityRecordsPage from "./pages/hr/MyQualityRecordsPage";
import ActiveBreaksPage from "./pages/hr/ActiveBreaksPage";
import InactivityReportPage from "./pages/hr/InactivityReportPage";
import FlaggedBreaksPage from "./pages/hr/FlaggedBreaksPage";
import JobOpenings from "./components/admin/recruitment/JobOpenings";
import Candidates from "./components/admin/recruitment/Candidates";
import RecruitmentKanban from "./components/admin/recruitment/RecruitmentKanban";
import VerifyAccount from "./components/VerifyAccount";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import CookieNotice from "./components/CookieNotice";
import AdminProtectedRoute from "./components/ProtectedRoute";
import TrainingProtectedRoute from "./components/training/TrainingProtectedRoute";
import TrainingWhiteboard from "./pages/TrainingWhiteboard";
// PR 1 (Phase B1) — guard for /hr/breaks/* (admin + TL + manager)
import BreakManagementProtectedRoute from "./components/BreakManagementProtectedRoute";
import { BreakManagementLayout } from "./components/hr/break/BreakManagementLayout";
import { CrmLayout, CrmProtectedRoute, CrmAdminOnly } from "./components/crm/CrmLayout";
import CrmHomePage, { CrmLeadsPage, CrmLeadDetailPage, CrmCompaniesPage, CrmCompanyDetailPage, CrmActivityPage } from "./pages/crm/CrmHomePage";
import CrmFollowUpWorkspacePage from "./pages/crm/CrmFollowUpWorkspacePage";
import { holdsTypingRole } from "./utils/heldRoles";
import CrmOpportunitiesPage, { CrmTeamActivityPage } from "./pages/crm/CrmOpportunitiesPage";
import CrmMyPerformancePage, { CrmSalesPerformancePage } from "./pages/crm/CrmPerformancePage";
import CrmMyWorkspacePage from "./pages/crm/CrmMyWorkspacePage";

// HR-PERSONA-001 (Jul 2026) — HR roles never see the operational order
// dashboard; any /dashboard* hit redirects to the Employee Directory.
// (Definition moved below the import block for import/first compliance.)
import { isJurisdictionRequestor } from "./utils/jurisdictionRequestorGuard";
// RBAC-PILOT-ORDERS-001: additive matrix grants (orders:view/create/update)
import { usePermissions } from "./hooks/usePermissions";
import { jrOrdersAccess } from "./utils/ordersMatrixAccess";
// WORKSPACE-SWITCHER-001
import { loadPermissions } from "./hooks/usePermissions";
import { postLoginRoute, clearActiveWorkspace, getActiveWorkspace } from "./utils/workspaces";
import WorkspaceChooser from "./pages/WorkspaceChooser";
// OD-PARITY-001 — Order Desk presentation (shell/look only; capabilities stay permission-gated)
import { useWorkspacePresentation } from "./hooks/useWorkspacePresentation";
// HEADER-BLUE-002 — operational roles that render the blue TitleFlow header (+ Order Desk presentation)
import AppHeader from "./components/shared/AppHeader";
import { WorkspaceShellGate } from "./components/shared/ModuleShell";
import { ROUTE_TO_VIEW, VIEW_TO_ROUTE, getDefaultRoute, isITRole, isQCSpecialistRole, isAccountingRole } from "./lib/dashboardViews";

// Analytics Layout (accessible to Team Leaders + Admin) — renamed from Reports (Jun 2026)
import ReportsLayout from "./components/ReportsLayout";

// Step-Up Authentication
import { StepUpAuthProvider, useStepUpAuth } from "./components/StepUpAuth";

// Analytics Components
import DowntimeAnalytics from "./components/analytics/DowntimeAnalytics";
import ProductivityAnalytics from "./components/analytics/ProductivityAnalytics";
import AnalyticsListPage from "./pages/AnalyticsListPage";
import PerformanceTargets from "./components/reports/PerformanceTargets";
import PolicyManagement from "./components/policies/PolicyManagement";

// Session Health Components - Phase 3
import { WorkProofPrompt, TeamSessionStatus } from "./components/session";
import useActivityTracker from "./hooks/useActivityTracker";

// Edit Request Components (Order Desk workflow)
import EditRequestModal from "./components/EditRequestModal";
import EditRequestsPanel from "./components/EditRequestsPanel";
import EditRequestsIndicator from "./components/EditRequestsIndicator";
import EditRequestsPage from "./components/EditRequestsPage";

// IT Operations Dashboard
import ITDashboard from "./pages/ITDashboard";
import QCAnalyticsPage from "./pages/QCAnalyticsPage";
import QCDashboard from "./pages/QCDashboard";
import TypingDashboard from "./pages/TypingDashboard";
import QCWorkspace from "./pages/QCWorkspace";

// HR Module Components - Phase 1 (2-Layer Dashboard Model)
import { 
  MyWorkspace, 
  TeamManagement 
} from "./components/hr";
import MyWorkspaceEmbedded from "./components/hr/MyWorkspaceEmbedded";
import TeamManagementEmbedded from "./components/hr/TeamManagementEmbedded";
import LeaveRegistry from "./components/hr/LeaveRegistry";

// Shared UI Components for FulfillmentTab
import { SectionHeader, EnhancedCard } from "./components/shared";

// Status display components
import { StatusBadge, normalizeStatusForOrderDesk } from "./components/StatusBadge";
import { PriorityIndicator } from "./components/PriorityIndicator";
import DashboardStats from './components/dashboard/DashboardStats';
import OpsHeroBanner from './components/dashboard/OpsHeroBanner';

import { 
  Plus,
  Search,
  Filter,
  TrendingUp,
  CheckCircle,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  Check,
  Settings,
  CalendarDays,
  ChevronLeft,
  Building,
  Upload,
  Link,
  Mail,
  Image,
  Edit,
  Download,
  Trash2,
  MoreVertical,
  Loader2,
  Lock,
  Eye,
  MapPin,
  Activity,
  MessageSquare,
  XCircle,
  Shield,
  Save,
  Calendar as CalendarIcon,
  LogIn,
  Key,
  UserPlus,
  ClipboardCheck,
  FileText as FileTextIcon,
  LayoutDashboard,
  ClipboardList,
  FolderOpen
} from "lucide-react";

// TITLEFLOW-ORDER-HEADER-UI-001 — icons for the shared Order Details tab bar
const ORDER_TAB_ICONS = { summary: FileTextIcon, overview: LayoutDashboard, fulfillment: ClipboardList, activity: Activity, files: FolderOpen };

// ANALYTICS-RENAME-001 (Jun 2026) — legacy /reports/* → /analytics/* redirect
// so existing bookmarks and links keep working after the module rename.
const LegacyReportsRedirect = () => {
  const location = useLocation();
  const target = location.pathname.replace(/^\/reports/, '/analytics') + location.search + location.hash;
  return <Navigate to={target} replace />;
};

// HR-PERSONA-001 (Jul 2026) — HR roles never see the operational order
// dashboard; any /dashboard* hit redirects to the Employee Directory.
// TEAM-MANAGEMENT-NAV-DUPLICATION-001: bare /dashboard/team (old "Team Management" link / bookmarks) → Leave Approvals tab,
// so the sidebar highlights the right item. Any explicit ?tab= is left untouched.
const TeamTabRedirect = ({ children }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!params.get('tab')) {
    params.set('tab', 'approvals');
    return <Navigate to={{ pathname: location.pathname, search: `?${params.toString()}` }} replace />;
  }
  return children;
};

const HRDashboardGuard = ({ children, allowRoles = [] }) => {
  let role = null;
  try { role = JSON.parse(localStorage.getItem('user') || '{}')?.role; } catch (_) { /* noop */ }
  if (['hr_manager', 'hr_specialist', 'hr'].includes(role) && !allowRoles.includes(role)) {
    return <Navigate to="/admin/hr/dashboard" replace />;
  }
  // CRM Phase 1 — Sales & Marketing never sees the operational dashboard.
  if (role === 'sales_marketing') {
    return <Navigate to="/crm" replace />;
  }
  return children;
};

// TEAM-SHELL-001 (Jun 2026) — IT/HR managers get Team Management inside
// their own portal shell at /admin/team; /dashboard/team stays for
// admin + regular Team Leaders (the TitleFlow shell IS their portal).
const TeamPortalGuard = ({ children }) => {
  let role = null;
  try { role = JSON.parse(localStorage.getItem('user') || '{}')?.role; } catch (_) { /* noop */ }
  if (['it_manager', 'it_support', 'hr_manager'].includes(role)) {
    return <Navigate to="/admin/team" replace />;
  }
  return children;
};

// Get backend URL with proper fallback for production
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// P0 SECURITY: Axios request interceptor to prevent stale token usage
// This catches cases where bfcache restores old axios headers after logout
if (isBrowser) {
  axios.interceptors.request.use(
    (config) => {
      // Check localStorage for current token state
      const currentToken = safeLocalStorage.get('token');
      
      // If no token in localStorage but Authorization header exists, we have stale state
      if (!currentToken) {
        // Remove any stale Authorization header
        delete config.headers['Authorization'];
        delete axios.defaults.headers.common['Authorization'];
        
        // If this is an authenticated endpoint (not login/public), redirect to login
        const publicPaths = ['/auth/login', '/auth/reset-password', '/auth/verify-email', '/health', '/ready'];
        const isPublicPath = publicPaths.some(path => config.url?.includes(path));
        
        if (!isPublicPath && !window.location.pathname.includes('/login')) {
          console.log('[Axios Interceptor] No token, redirecting to login');
          window.location.replace('/login');
          // Cancel the request
          return Promise.reject(new axios.Cancel('No auth token'));
        }
      } else {
        // AUTH-RESILIENCE (Feb 2026) — Removed client-side JWT `exp`
        // self-rejection. The backend's get_current_user is the
        // authority on expiry and returns 401 with
        // `X-Logout-Reason: token_expired` when needed. Client-side
        // enforcement created false-positive logouts when the client
        // clock was even slightly fast vs the server, OR when a
        // token was issued seconds before the request fired
        // (login → first call race). The response interceptor below
        // handles real expiry via the X-Logout-Reason header.
        config.headers['Authorization'] = `Bearer ${currentToken}`;
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  // Response interceptor — AUTH-RESILIENCE (Feb 2026)
  //
  // Previous behaviour: ANY 401 cleared the token + redirected to
  // /login. That meant a single Atlas/Redis hiccup logged the user
  // out, because get_current_user used to convert all infra
  // exceptions into 401.
  //
  // New behaviour:
  //   1. The backend now returns 503 for infra failures — those
  //      bypass this block entirely (the request will be retried
  //      naturally on the next poll).
  //   2. We only treat a 401 as a real logout when:
  //        - it carries an `X-Logout-Reason` header
  //          (idle_timeout / role_changed / password_changed /
  //          token_expired / token_revoked / invalid_token), OR
  //        - its `detail` matches one of the known logout strings
  //          (defence against edge proxies that strip headers).
  //   3. Anonymous "Could not validate credentials" 401s on
  //      background polls now WARN + ignore (no logout, no
  //      redirect). The first time the user actually triggers an
  //      action, the response will either succeed (transient
  //      blip resolved) or come back with a labelled 401 that
  //      legitimately ends the session.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      if (status !== 401) {
        return Promise.reject(error);
      }

      const headerReason = (error.response.headers?.['x-logout-reason'] || '').toLowerCase();
      const detail = (error.response.data?.detail || '').toString().toLowerCase();
      const LABELLED_REASONS = new Set([
        'idle_timeout',
        'role_changed',
        'password_changed',
        'token_expired',
        'token_revoked',
        'invalid_token',
      ]);
      const detailMatchesLogout =
        detail.includes('session expired due to inactivity') ||
        detail.includes('role was updated') ||
        detail.includes('password has been changed') ||
        detail.includes('token has been revoked') ||
        detail.includes('token has expired');

      const isRealLogout =
        LABELLED_REASONS.has(headerReason) || detailMatchesLogout;

      if (!isRealLogout) {
        // Unlabelled 401 — most likely a transient validation hiccup
        // (e.g., race with role refresh, or backend was mid-restart).
        // Do NOT clear the token; do NOT redirect. Surface the error
        // to the caller so individual code paths can decide what to
        // do — most will just retry on the next poll tick.
        console.warn(
          '[Axios Interceptor] Unlabelled 401 ignored to prevent ' +
          'transient-failure logout. URL:', error.config?.url
        );
        return Promise.reject(error);
      }

      // Jun 2026 — IDLE-timeout 401s must NOT hard-redirect. The user
      // may still rescue the session within the server-side grace
      // window, and the product spec requires a warning modal with
      // "Stay Signed In" before any logout. Defer to IdleManager
      // (same contract as utils/adminApi.js): it shows the warning
      // modal, runs the countdown, and performs the logout itself if
      // the countdown expires or the session can't be extended.
      const isIdleTimeout401 =
        headerReason === 'idle_timeout' || detail.includes('inactivity');
      if (isIdleTimeout401) {
        console.warn(
          '[Axios Interceptor] Idle-timeout 401 — deferring to ' +
          'IdleManager warning modal. URL:', error.config?.url
        );
        // IDLE-TOAST-SUPPRESS-001 (Jun 2026): tag the error + stamp a global
        // marker so downstream catch blocks / the toast wrapper below can
        // suppress generic "Error fetching …" toasts for 401s that are
        // already being handled by the "Are you still there?" modal.
        error.isIdleTimeout401 = true;
        try { window.__TF_IDLE401_LAST = Date.now(); } catch (_) { /* non-browser */ }
        try {
          window.dispatchEvent(new CustomEvent('idle-timeout-401', {
            detail: { source: 'axios', url: error.config?.url }
          }));
        } catch (_) { /* non-browser env */ }
        return Promise.reject(error);
      }

      // Real logout path — queue a single unified notice that the
      // Login page will render after the redirect. This replaces
      // the previous direct `toast.info(...)` which was wiped by
      // the page reload before the user could see it.
      const reasonForToast = headerReason ||
        (detail.includes('inactivity') ? 'idle_timeout' :
         detail.includes('role was updated') ? 'role_changed' :
         detail.includes('password has been changed') ? 'password_changed' :
         'session_invalid');
      try {
        queueLogoutNotice(reasonForToast);
      } catch (_) { /* helper not loaded yet — extremely unlikely */ }
      try {
        const { clearPermissionsCache } = require('./hooks/usePermissions');
        clearPermissionsCache?.();
      } catch (_) { /* hook not loaded */ }

      console.log(`[Axios Interceptor] Real 401 logout (${reasonForToast})`);
      safeLocalStorage.remove('token');
      safeLocalStorage.remove('refresh_token');
      safeLocalStorage.remove('user');
      delete axios.defaults.headers.common['Authorization'];

      if (!window.location.pathname.includes('/login')) {
        window.location.replace('/login');
      }
      return Promise.reject(error);
    }
  );
}

// Add global error handlers
if (isBrowser) {
  window.addEventListener('error', (e) => {
    console.error('Global error:', e.error || e);
  });
  
  window.addEventListener('unhandledrejection', (e) => {
    console.error('Unhandled promise rejection:', e.reason);
  });
  
  // SECURITY: Global bfcache handler — catch Back/Forward restoration on ANY page
  // If the browser restores a protected page from bfcache and there's no valid token,
  // force redirect to login immediately
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      const token = safeLocalStorage.get('token');
      if (!token) {
        const protectedPrefixes = ['/dashboard', '/orders', '/workspace', '/team', '/reports', '/analytics', '/admin', '/billing', '/qc-dashboard', '/qc-analytics'];
        const isProtected = protectedPrefixes.some(p => window.location.pathname.startsWith(p));
        if (isProtected) {
          console.log('[Global] bfcache restored protected page without token — redirecting');
          window.location.replace('/login');
        }
      }
    }
  });
}

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { useAuth };

// ===============================
// GLOBAL UTILITY FUNCTIONS
// ===============================

// Account timezone configuration - U.S. Eastern Time
const ACCOUNT_TZ = 'America/New_York';

/**
 * Format UTC timestamp to Eastern Time (ET) consistently across the application
 * Converts UTC timestamp to America/New_York timezone and formats as US 12-hour time
 * Handles DST automatically (shows EST in winter, EDT in summer)
 * @param {string} isoUtc - ISO 8601 timestamp with Z (UTC)
 * @param {boolean} includeSeconds - Whether to include seconds in output (default: true)
 * @returns {string} Formatted timestamp in MM/DD/YYYY hh:mm:ss A ET format
 */
/**
 * Format UTC timestamp to Eastern Time (ET)
 * Uses date-fns-tz for reliable timezone conversion
 * @param {string} isoUtc - ISO 8601 timestamp with Z (UTC)
 * @param {boolean} includeSeconds - Whether to include seconds (default: true)
 * @returns {string} Formatted timestamp with " ET" suffix
 */
/**
 * Ensure timestamp has timezone indicator for proper UTC→ET conversion.
 * Backend may return naive datetimes (no Z or offset) — treat them as UTC.
 */
const ensureUtc = (isoStr) => {
  if (!isoStr) return null;
  const s = String(isoStr);
  // Already has timezone info (Z, +00:00, -04:00, etc.)
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) return s;
  // Naive datetime — append Z to mark as UTC
  return s + 'Z';
};

export const formatUtcToET = (isoUtc, includeSeconds = true) => {
  if (!isoUtc) return 'N/A';
  
  try {
    const formatString = includeSeconds ? 'MM/dd/yyyy hh:mm:ss a' : 'MM/dd/yyyy hh:mm a';
    return formatInTimeZone(ensureUtc(isoUtc), ACCOUNT_TZ, formatString) + ' ET';
  } catch (error) {
    console.error('Error formatting timestamp to ET:', error, isoUtc);
    return 'Invalid date';
  }
};

// Alias for backward compatibility
const formatTimestamp = formatUtcToET;

// ===============================
// CURRENCY FORMATTING UTILITIES
// ===============================

/**
 * Format a numeric value as currency with 2 decimal places
 * @param {string|number} value - The value to format
 * @returns {string} Formatted currency string (e.g., "500.00")
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
  if (isNaN(num)) return '';
  return num.toFixed(2);
};

/**
 * Parse and normalize a currency input value
 * Removes non-numeric characters except decimal point and negative sign
 * @param {string} value - Raw input value
 * @returns {string} Normalized numeric string
 */
const parseCurrencyInput = (value) => {
  if (!value) return '';
  // Remove $ sign and any other non-numeric chars except . and -
  return value.replace(/[^0-9.-]/g, '');
};

/**
 * Handle currency field change - allows typing while preserving numeric value
 * @param {string} value - Input value from user
 * @returns {string} Cleaned numeric string for state
 */
const handleCurrencyChange = (value) => {
  if (!value) return '';
  // Allow digits, one decimal point, and optional leading minus
  const cleaned = value.replace(/[^0-9.-]/g, '');
  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  return cleaned;
};

/**
 * Handle currency field blur - format to 2 decimal places
 * @param {string} value - Current field value
 * @returns {string} Formatted currency value with 2 decimals
 */
const handleCurrencyBlur = (value) => {
  if (!value || value === '' || value === '-') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  // Round to 2 decimal places
  return num.toFixed(2);
};

/**
 * Get tooltip info for timestamp (shows both ET and UTC for audit clarity)
 * @param {string} isoUtc - ISO 8601 timestamp with Z (UTC)
 * @returns {string} Tooltip text with ET time and UTC for verification
 */
const getTimestampTooltip = (isoUtc) => {
  if (!isoUtc) return '';
  
  try {
    // Format as Eastern Time (already includes " ET" suffix)
    const etTime = formatUtcToET(isoUtc);
    
    // Ensure isoUtc is a valid ISO string
    const utcTime = typeof isoUtc === 'string' ? isoUtc : new Date(isoUtc).toISOString();
    
    // Show both ET and UTC for audit trail
    return `${etTime}\nUTC: ${utcTime}`;
  } catch (error) {
    console.error('Error creating tooltip:', error);
    return '';
  }
};


/**
 * Format property address with county and state for enhanced display
 * Used in both dashboard and reports modules
 * @param {Object} order - Order object containing property data
 * @returns {string} Formatted address in "Address, County, State" format
 */
const formatPropertyAddress = (order) => {
  if (!order) return 'N/A';
  
  try {
    // Priority 1: Use pre-formatted display address from backend (includes Street, City, State ZIP)
    if (order.property_address_display) {
      return order.property_address_display;
    }
    
    // Priority 2: Construct full address from parts
    const parts = [];
    
    // Add street address
    const street = order.property_address || order.property_address_full || order.property_address_line1;
    if (street && street !== 'N/A') {
      parts.push(street);
    }
    
    // Add city
    if (order.property_city && order.property_city !== 'N/A') {
      parts.push(order.property_city);
    }
    
    // Add state
    const state = order.property_state || order.state;
    if (state && state !== 'N/A') {
      // Add ZIP to state if available
      const zip = order.property_zip || order.property_zip_code;
      if (zip && zip !== 'N/A') {
        parts.push(`${state} ${zip}`);
      } else {
        parts.push(state);
      }
    }
    
    return parts.length > 0 ? parts.join(', ') : 'N/A';
    
  } catch (error) {
    console.error('Error formatting property address:', error);
    return order?.property_address || 'N/A';
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Safe token retrieval
    return safeLocalStorage.get('token');
  });
  const [loading, setLoading] = useState(true);

  // Role-change watcher (Feb 27, 2026) — lightweight background poll
  // that catches out-of-band role updates while the user has the app
  // open in a background tab. See hooks/useRoleChangeWatcher.js for
  // the cost profile (≤ 1 req/min while visible, paused while hidden).
  useRoleChangeWatcher(user, () => {
    try {
      const { clearPermissionsCache } = require('./hooks/usePermissions');
      clearPermissionsCache?.();
    } catch (_) { /* hook not loaded */ }
    safeLocalStorage.remove('token');
    safeLocalStorage.remove('refresh_token');
    safeLocalStorage.remove('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    if (!window.location.pathname.includes('/login')) {
      window.location.replace('/login');
    }
  });

  // P0 SECURITY: Sync React state with localStorage on page restore (bfcache)
  // This catches cases where localStorage was cleared but React state still has old token
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        console.log('[AuthProvider] Page restored from bfcache, syncing token state');
        const currentToken = safeLocalStorage.get('token');
        if (!currentToken) {
          console.log('[AuthProvider] No token in localStorage after bfcache restore');
          // Clear React state
          setToken(null);
          setUser(null);
          setLoading(false);
          // Force redirect to login - don't wait for ProtectedRoute
          if (window.location.pathname !== '/login' && 
              window.location.pathname !== '/verify-email' &&
              window.location.pathname !== '/reset-password') {
            console.log('[AuthProvider] Forcing redirect to login');
            window.location.replace('/login');
          }
        }
      }
    };
    
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    if (token && API) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    if (!API) {
      console.error('API URL not configured');
      logout();
      return;
    }
    
    try {
      const response = await dedupedGet(`${API}/auth/me`);
      setUser(response.data);
      // Feb 27, 2026: keep localStorage.user in lock-step with React
      // state. Legacy admin guards and toast helpers read from
      // localStorage directly; without this refresh they'd see the
      // role/permissions snapshot captured at login and never the
      // post-role-change values.
      try {
        safeLocalStorage.set('user', JSON.stringify(response.data));
      } catch (_) { /* storage quota / disabled — non-fatal */ }
      
      // Set Sentry user context for error attribution
      if (window.__SENTRY_INITIALIZED__ !== false) {
        try {
          const SentryModule = require('@sentry/react');
          SentryModule.setUser({
            id: response.data.id,
            email: response.data.email,
            role: response.data.role,
            username: response.data.full_name || response.data.email,
          });
        } catch (e) { /* Sentry not loaded */ }
      }
    } catch (error) {
      console.warn('Failed to fetch user:', error);
      // CRITICAL FIX: Only logout on 401 (actual auth failure)
      // Don't logout on network errors, timeouts, or other transient failures
      // This prevents logout loops during page transitions/reloads
      if (error.response?.status === 401) {
        console.log('[AuthProvider] Token invalid (401), logging out');
        logout();
      } else {
        // For network errors or other failures, just clear user but keep token
        // User can retry or the token might still be valid
        console.log('[AuthProvider] Non-401 error, keeping token but clearing user state');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    if (!API) {
      return { success: false, code: 'config', message: 'Application is not configured correctly. Please contact support.' };
    }
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, refresh_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      safeLocalStorage.set('token', access_token);
      resetLogoutNoticeState(); // LOGIN-NOTICE-STALE-001 — a fresh session never inherits a stale notice
      
      // Store user data for AdminProtectedRoute to access
      safeLocalStorage.set('user', JSON.stringify(userData));
      
      // Phase 1 Security: Store refresh token
      if (refresh_token) {
        safeLocalStorage.set('refresh_token', refresh_token);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Set flag to show welcome banner (will be checked on dashboard mount)
      sessionStorage.setItem('showWelcomeBanner', 'true');
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      
      // LOGIN-INLINE-ERROR (Aug 2026): return a structured result so the
      // login page can show a persistent inline message near the form
      // (transient corner toasts were easy to miss). Never reveal whether
      // the email or the password specifically was wrong.
      const status = error.response?.status;
      const detail = String(error.response?.data?.detail || '');
      
      if (status === 401 || status === 403) {
        const lower = detail.toLowerCase();
        if (lower.includes('disabled')) {
          return { success: false, code: 'account', message: 'Your account is disabled. Please contact your administrator.' };
        }
        if (lower.includes('verify')) {
          return { success: false, code: 'account', message: detail };
        }
        return { success: false, code: 'invalid', message: 'Invalid email address or password. Please try again.' };
      }
      if (status === 429) {
        return { success: false, code: 'rate_limited', message: 'Too many login attempts. Please wait a few minutes and try again.' };
      }
      if (status === 500 || status === 502 || status === 503 || status === 504) {
        return { success: false, code: 'server', message: 'Server temporarily unavailable. Please try again in a moment.' };
      }
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
        return { success: false, code: 'network', message: 'Unable to connect to the server. Please check your connection and try again.' };
      }
      return { success: false, code: 'unknown', message: 'Login failed. Please try again or contact support if the issue persists.' };
    }
  };

  // Feb 2026 — callers that handle their own SPA navigation (e.g. the
  // Dashboard `handleLogout`) can pass `{ skipRedirect: true }` so the
  // unified glassmorphism logout-notice toast can render on /login
  // without being killed by a full bundle reload. All existing callers
  // continue to work because the option defaults to false (= legacy
  // `window.location.replace('/login')` behavior).
  const logout = async ({ skipRedirect = false, reason = 'manual' } = {}) => {
    // AUTH-RESILIENCE (Feb 2026) — Fire-and-forget logout.
    //
    // Previous behaviour: awaited POST /api/auth/logout BEFORE
    // clearing local state. If Atlas was slow (we've seen 5-25s on
    // close_attendance_session under load), the UI froze on the
    // current page that long.
    //
    // New sequence:
    //   1. Suppress toast noise from in-flight polls that will 401.
    //   2. Snapshot the tokens.
    //   3. Clear ALL local state, axios defaults, Sentry context.
    //   4. Set the post-logout banner flag.
    //   5. Redirect (window.location.replace) IMMEDIATELY.
    //   6. Send POST /api/auth/logout AFTER redirect, with a hard
    //      4s timeout and no .await — purely fire-and-forget. The
    //      backend will blacklist the token; if the call fails or
    //      times out, the JWT still expires naturally.
    try {
      const { suppressToastsDuringLogout } = require('./utils/logoutToastSuppressor');
      suppressToastsDuringLogout();
    } catch (_) { /* utility absent in some test bundles */ }
    // PREMIUM-NOTIFY-001 — "Signing you out…" appears right after stale toasts are cleared; the Login page
    // completes it ("Successfully signed out"). toast.custom is not suppressed. Logout proceeds regardless.
    if (reason === 'manual') {
      try { require('./utils/glassmorphismToasts').showLogoutInProgress(); } catch (_) { /* toast layer absent */ }
    }

    // 2. Snapshot tokens BEFORE wiping local state so the
    //    fire-and-forget call below still has credentials.
    const token = safeLocalStorage.get('token');
    const refresh_token = safeLocalStorage.get('refresh_token');

    // 3. Clear local React state
    setUser(null);
    setToken(null);

    // Clear permissions singleton cache (RBAC isolation between sessions)
    try {
      const { clearPermissionsCache } = require('./hooks/usePermissions');
      clearPermissionsCache();
    } catch (e) { /* hook module not loaded yet */ }
    clearActiveWorkspace(); // WORKSPACE-SWITCHER-001

    // Clear Sentry user context
    try {
      const SentryModule = require('@sentry/react');
      SentryModule.setUser(null);
    } catch (e) { /* Sentry not loaded */ }

    // Clear all auth-related storage
    safeLocalStorage.remove('token');
    safeLocalStorage.remove('refresh_token');
    safeLocalStorage.remove('user');
    delete axios.defaults.headers.common['Authorization'];

    // Clear any cached navigation state
    safeLocalStorage.remove('lastRoute');
    safeLocalStorage.remove('redirectAfterLogin');

    // Clear ALL sessionStorage to prevent any cached state
    sessionStorage.clear();

    // 4. Queue the unified logout notice. The Login page will render
    //    a single glassmorphism toast (showGlassmorphismLogoutNotice)
    //    on mount. Replaces the legacy `sessionStorage.showLogoutBanner`
    //    flag — see utils/logoutNotice.js for the full lifecycle.
    queueLogoutNotice('manual', { force: true }); // token already cleared above — deliberate user action

    // 6. Reliable backend close. Beacon-first so we survive the
    //    `window.location.replace('/login')` unload race that the
    //    legacy axios.post() couldn't (XHR gets aborted when the
    //    document is replaced). `sendBeacon` is delivery-guaranteed
    //    by the browser; fetch keepalive is the fallback; axios is
    //    the final fallback.
    if (token) {
      const beaconUrl = `${API}/auth-v2/logout/beacon`;
      const payload = JSON.stringify({ token, source: reason });
      let sent = false;
      try {
        if (navigator.sendBeacon) {
          sent = navigator.sendBeacon(
            beaconUrl,
            new Blob([payload], { type: 'application/json' })
          );
        }
      } catch (e) {
        // sendBeacon can throw under quota / size restrictions — fall
        // through to fetch keepalive below.
        console.warn('[logout] sendBeacon threw:', e?.message || e);
      }
      if (!sent) {
        try {
          // fetch keepalive survives navigation in modern browsers.
          fetch(beaconUrl, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: payload,
          }).catch(() => { /* best-effort */ });
          sent = true;
        } catch (_) { /* fall through */ }
      }
      if (!sent) {
        // Last-resort legacy path. Same fire-and-forget axios as
        // before — preserved for environments without sendBeacon or
        // keepalive, OR if both threw synchronously.
        axios.post(`${API}/auth/logout`,
          { refresh_token, reason },
          {
            headers: { 'Authorization': `Bearer ${token}` },
            timeout: 4000,
          }
        ).catch((err) => {
          console.warn('[logout] axios fallback failed:',
                       err?.code || err?.response?.status || err?.message);
        });
      }
    }

    // 5. Redirect. Callers that pass `{ skipRedirect: true }` will
    //    SPA-navigate themselves so the unified logout notice toast
    //    can render on /login without being killed by a bundle reload.
    if (skipRedirect) return;
    window.location.replace('/login');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// ZIP Code Lookup Utility Functions
const zipCodeAPI = {
  // Main ZIP code lookup function
  lookupZipCode: async (zipCode) => {
    try {
      const response = await axios.post(`${API}/zip/lookup`, {
        zip_code: zipCode.trim()
      });
      
      return response.data;
    } catch (error) {
      console.error('ZIP code lookup error:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required for ZIP code lookup');
      } else if (error.response?.status === 500) {
        throw new Error('ZIP code lookup service temporarily unavailable');
      } else {
        throw new Error('ZIP code lookup failed. Please try again.');
      }
    }
  },

  // Auto-populate form fields based on ZIP code lookup
  autoPopulateFromZip: async (zipCode, setFormFields, showToast = true) => {
    if (!zipCode || zipCode.length < 5) {
      return null;
    }
    
    try {
      const result = await zipCodeAPI.lookupZipCode(zipCode);
      
      if (result.is_valid && result.results && result.results.length > 0) {
        const primaryCity = result.primary_city || result.results[0];
        
        // Auto-populate fields
        const updates = {
          city: primaryCity.city,
          state: primaryCity.state,
          county: primaryCity.county
        };
        
        setFormFields(prevFields => ({
          ...prevFields,
          ...updates
        }));
        
        // Auto-fill silently - no notification popup needed
        // Fields are populated above without user interruption
        
        return {
          success: true,
          data: result,
          primaryCity: primaryCity,
          multipleCities: result.results.length > 1
        };
        
      } else {
        if (showToast) {
          // Handle invalid ZIP or service errors gracefully
          // Show user-friendly message instead of technical error
          toast.error('Unable to auto-fill from ZIP code. Please enter city, state, and county manually.');
        }
        return {
          success: false,
          error: result.error_message || 'Invalid ZIP code'
        };
      }
      
    } catch (error) {
      if (showToast) {
        toast.error(error.message);
      }
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// Enhanced ZIP Code Input Component with Auto-Lookup
const ZipCodeInput = ({ 
  value, 
  onChange, 
  onZipLookup, 
  placeholder = "Enter ZIP code", 
  className = "",
  disabled = false 
}) => {
  const [isLooking, setIsLooking] = useState(false);
  
  const handleZipChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Auto-lookup when ZIP is complete (5 digits)
    if (newValue.length === 5 && /^\d{5}$/.test(newValue)) {
      handleAutoLookup(newValue);
    }
  };
  
  const handleAutoLookup = async (zipCode) => {
    setIsLooking(true);
    try {
      if (onZipLookup) {
        await onZipLookup(zipCode);
      }
    } finally {
      setIsLooking(false);
    }
  };
  
  return (
    <div className="relative">
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleZipChange}
        className={className}
        disabled={disabled || isLooking}
        maxLength={10}
      />
      {isLooking && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

// Login Component
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // LOGIN-INLINE-ERROR (Aug 2026): persistent error banner near the form
  const [errorMsg, setErrorMsg] = useState('');
  // logoutMessage state removed — Feb 2026 unified logout notice.
  // The toast is the single notification surface; no inline banner.
  const [bgLoaded, setBgLoaded] = useState(false);
  // LOGIN-PREMIUM-UI: "Remember me" is UI-only — it prefills the email on this device.
  const [rememberMe, setRememberMe] = useState(() => !!safeLocalStorage.get('login_remember_email'));
  useEffect(() => {
    const saved = safeLocalStorage.get('login_remember_email');
    if (saved) setEmail(saved);
  }, []);
  useEffect(() => {
    if (rememberMe && email) safeLocalStorage.set('login_remember_email', email);
    else if (!rememberMe) safeLocalStorage.remove('login_remember_email');
  }, [rememberMe, email]);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // SECURITY FIX: If user arrives at /login with a valid token, redirect to dashboard.
  // If the token is expired/invalid, clean it up so session is fresh.
  useEffect(() => {
    const token = safeLocalStorage.get('token');
    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const exp = payload.exp;
          const now = Math.floor(Date.now() / 1000);
          if (exp && exp > now + 30) {
            // Token is still valid — redirect (user is already authenticated).
            // ADMIN-HOME-001: admins go to the Admin Dashboard.
            let target = '/dashboard';
            try {
              const u = JSON.parse(safeLocalStorage.get('user') || '{}');
              if (u?.role === 'admin') target = '/admin';
            } catch (_) { /* keep default */ }
            navigate(target, { replace: true });
            return;
          }
        }
      } catch (e) {
        // Token decode failed — fall through to cleanup
      }
      // Token exists but is expired or malformed — clear stale auth
      safeLocalStorage.remove('token');
      safeLocalStorage.remove('refresh_token');
      safeLocalStorage.remove('user');
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []); // Run once on mount

  // No-scroll on login page + dark fallback bg
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.backgroundColor = '#071f5c';
    return () => {
      document.body.style.overflow = '';
      document.body.style.backgroundColor = '';
    };
  }, []);

  // Unified logout notice — Feb 2026 UX cleanup.
  // Replaces:
  //   - the old `sessionStorage.showLogoutBanner` glass toast (manual)
  //   - the old `localStorage.logoutReason` inline blue banner (idle/expiry)
  //   - the axios interceptor's `toast.info(...)` (token/role/revoked)
  // Single source of truth: utils/logoutNotice.js. Single render
  // surface: showGlassmorphismLogoutNotice(notice).
  //
  // Reliability notes (Feb 2026, round 4):
  //   - We need a small `setTimeout(250)` so the sonner Toaster has
  //     finished mounting + subscribing before we fire `toast.custom`.
  //     Synchronous fire from useEffect raced the Toaster mount on
  //     the live preview build.
  //   - readLogoutNotice() is single-shot — it deletes the storage
  //     key on the first read. A StrictMode-induced second mount
  //     will see null and skip. So no extra guard is needed.
  //   - Critically: we do NOT return a cleanup function that clears
  //     the timer. StrictMode's synthetic unmount would otherwise
  //     swallow the toast.
  //   - Diagnostic logs are deliberate. If you ever see
  //     "[logout-notice] no notice queued" right after a logout,
  //     queueLogoutNotice() didn't run upstream.
  useEffect(() => {
    try {
      const notice = readLogoutNotice();
      if (notice) {
        // Round 7 timing: defer 800ms so sonner Toaster is fully mounted +
        // subscribed before we fire toast.custom(). The previous 250ms
        // was racing the bundle reload + Toaster init on Chrome MacOS,
        // and sonner does not replay queued toasts when its Toaster
        // mounts after a toast was already added to the store.
        const fire = () => {
          showGlassmorphismLogoutNotice(notice);
          clearLogoutNotice(); // PREMIUM-NOTIFY-001 — consume only once the toast is actually shown
          markLogoutNoticeShown(notice.scenario); // LOGIN-NOTICE-STALE-001 — no replay on refresh
        };
        const checkAndRetry = () => {
          const painted = document.querySelectorAll('[data-sonner-toast]').length > 0;
          if (!painted) {
            fire();
          }
        };
        setTimeout(() => {
          try {
            fire();
            // Check 700ms later (≈ 1500ms total after mount). If sonner
            // hasn't painted yet (race against late Toaster mount), fire
            // a second time. Idempotent because of sonner's id dedupe
            // and our notice was already consumed → still safe.
            setTimeout(checkAndRetry, 700);
          } catch (_) { /* sonner not ready; the retry timer will catch it */ }
        }, 800);
      }
    } catch (_) { /* readLogoutNotice failed; user just won't see the toast */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    
    const result = await login(email, password);
    if (result.success) {
      // Get user role from localStorage (set during login)
      let targetRoute = '/dashboard';
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const role = userData?.role;
        
        // Role-based redirect
        switch (role) {
          case 'admin':
            // ADMIN-HOME-001 (Jun 2026): admins land on the Admin Dashboard
            targetRoute = '/admin';
            break;
          case 'jurisdiction_manager':
            targetRoute = '/admin/jurisdictions/dashboard';
            break;
          case 'jurisdiction_requestor':
            // Session 24 (Feb 22, 2026): requestor has no access to the
            // operational order dashboard. Land them on their dedicated
            // lightweight dashboard.
            targetRoute = '/admin/my-jurisdiction-dashboard';
            break;
          case 'it_manager':
          case 'it_support':
            targetRoute = '/admin/it-dashboard';
            break;
          case 'qc_specialist':
            targetRoute = '/qc-dashboard';
            break;
          case 'typing_processor':
            targetRoute = '/dashboard/typing';
            break;
          case 'processor':
          case 'tax_processor':
          case 'title_search_processor':
          case 'search_processor':
            targetRoute = '/dashboard/processor-orders';
            break;
          default:
            targetRoute = '/dashboard';
        }

        // Defense-in-depth (Feb 25, 2026): if the canonical `role`
        // field doesn't match (e.g. legacy/cached record with the
        // role hidden in `roles[]` or `role_ids[]`), still route the
        // jurisdiction_requestor persona to their dashboard.
        if (isJurisdictionRequestor(userData)) {
          targetRoute = '/admin/my-jurisdiction-dashboard';
        }
      } catch (e) {
        console.error('Error getting user role for redirect:', e);
      }

      // WORKSPACE-SWITCHER-001: fresh login → forget the previous choice; users
      // with 2+ workspaces pick one first (same JWT/session — pure navigation).
      clearActiveWorkspace();
      try {
        const perms = await loadPermissions(localStorage.getItem('token'));
        targetRoute = postLoginRoute(JSON.parse(localStorage.getItem('user') || '{}'), perms, targetRoute);
      } catch (e) {
        console.error('Workspace resolution failed, using role route:', e);
      }
      
      navigate(targetRoute, { replace: true });
    } else {
      setErrorMsg(result.message);
    }
    
    setLoading(false);
  };

  return (
    <div className="relative overflow-hidden bg-[#eaf3fc] flex-1" data-testid="login-page">
      <LoginNetworkBackdrop loaded={bgLoaded} onLoad={() => setBgLoaded(true)} />

      {/* Centered content */}
      <div className="lp-stage relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-32px)] px-4 py-6">
        {/* TitleFlow Logo — brand colours untouched */}
        <div className="lp-logo-wrap mb-8 lp-enter flex flex-col items-center">
          <TitleFlowLogo
            variant="full"
            className="lp-logo"
            style={{
              width: 'auto',
              filter: 'drop-shadow(0 6px 18px rgba(255,255,255,0.9))',
              display: 'block',
            }}
            data-testid="login-logo"
          />
        </div>

        {/* Login Card */}
        <div className="lp-glass lp-enter lp-enter-delay rounded-2xl px-8 pt-7 pb-7 w-full max-w-[520px]" data-testid="login-card">
          <h1 className="text-center text-white text-2xl sm:text-3xl font-semibold tracking-tight">Welcome back,</h1>
          <p className="text-center text-white/80 text-base mt-1 mb-6">Please login to your account.</p>

          {/* LOGIN-INLINE-ERROR (Aug 2026): persistent, replaces itself on
              each attempt (no duplicates), never reveals which field failed */}
          {errorMsg && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-2.5 rounded-lg border border-red-400/50 bg-red-500/25 px-4 py-3 text-sm text-red-50"
              data-testid="login-error-message"
            >
              <svg className="h-5 w-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white font-semibold text-sm">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" aria-hidden="true" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="lp-input h-12 pl-10 text-sm sm:text-base rounded-lg placeholder:text-slate-400"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white font-semibold text-sm">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" aria-hidden="true" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="lp-input h-12 pl-10 text-sm sm:text-base rounded-lg placeholder:text-slate-400"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2.5 text-white cursor-pointer select-none" data-testid="login-remember-me">
                <input
                  type="checkbox"
                  className="lp-check"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  data-testid="login-remember-me-checkbox"
                />
                Remember me
              </label>
              <a href="/support" className="text-white/90 hover:text-white underline-offset-4 hover:underline transition-colors" data-testid="login-forgot-password-link">
                Forgot Password?
              </a>
            </div>

            <Button
              type="submit"
              className="lp-signin w-full h-12 font-semibold mt-2 text-base text-white rounded-xl border-0"
              disabled={loading}
              isLoading={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : (<span className="inline-flex items-center gap-2">Sign In <span aria-hidden="true">→</span></span>)}
            </Button>
          </form>
        </div>

        {/* Branding / motivational message */}
        <div className="lp-tag-wrap mt-8 flex flex-col items-center gap-3 lp-enter lp-enter-delay text-center px-4" data-testid="login-tagline">
          <p className="lp-tagline text-2xl sm:text-3xl font-medium tracking-tight">
            Every search. Every detail. <em>Every time.</em>
          </p>
          <div className="lp-rule w-full max-w-md" />
          <p className="lp-subline text-[11px] sm:text-xs font-semibold uppercase" data-testid="login-tagline-sub">
            Search <span className="mx-2 lp-dot">•</span> Process <span className="mx-2 lp-dot">•</span> Deliver <span className="mx-2 lp-dot">•</span> Together
          </p>
        </div>
      </div>


      {/* Login footer — transparent over the light backdrop */}
      <div className="lp-footer relative z-10 px-6 py-2">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-1 text-xs">
          <span>&copy; 2026 PNA Title Services LLC. All rights reserved.</span>
          <nav className="flex items-center gap-1.5">
            <a href="/privacy-policy">Privacy Policy</a>
            <span className="lp-sep">|</span>
            <a href="/cookie-policy">Cookie Policy</a>
            <span className="lp-sep">|</span>
            <a href="/terms-of-use">Terms of Use</a>
            <span className="lp-sep">|</span>
            <a href="/security">Security Notice</a>
            <span className="lp-sep">|</span>
            <a href="/support">Contact Support</a>
          </nav>
        </div>
      </div>

      {/* Jul 2026 — one-time essential-cookies notice (see components/CookieNotice.jsx) */}
      <CookieNotice />
    </div>
  );
};

// Dashboard Stats Component - imported from dedicated module with retry/stale-data resilience
// (imports moved to the top import block for import/first compliance)


// OrdersManagement component has been extracted to /app/frontend/src/pages/OrdersManagement.jsx


// Simplified Order Actions - Replace inline editing with "View/Edit" button
// Order Details Modal Component


// ReportingDashboard is now imported from ./components/reporting/ReportingDashboard

// Activity History Panel Component
const ActivityHistoryPanel = ({ history }) => {
  const getActivityIcon = (activityType) => {
    const type = (activityType || '').toLowerCase();
    if (type.includes('created')) return '✨';
    if (type.includes('status')) return '🔄';
    if (type.includes('assign')) return '👤';
    if (type.includes('priority')) return '⚡';
    if (type.includes('edit') || type.includes('update') || type.includes('field')) return '✏️';
    if (type.includes('file') || type.includes('upload')) return '📎';
    return '📝';
  };

  const getActivityColor = (activityType) => {
    const type = (activityType || '').toLowerCase();
    if (type.includes('created')) return 'text-green-600';
    if (type.includes('status')) return 'text-purple-600';
    if (type.includes('assign')) return 'text-indigo-600';
    if (type.includes('priority')) return 'text-orange-600';
    if (type.includes('edit') || type.includes('update') || type.includes('field')) return 'text-blue-600';
    return 'text-slate-600';
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Activity History</h3>
      
      {history.length === 0 ? (
        <p className="text-slate-500 text-center py-8">No activity history available.</p>
      ) : (
        <div className="space-y-3">
          {history.map((activity) => {
            // Map activity_events fields to display fields
            const activityType = activity.type || activity.activity_type || '';
            const description = activity.summary || activity.description || '';
            const userName = activity.actor?.name || activity.user_name || activity.actor_name || '';
            const timestamp = activity.created_at || activity.timestamp;

            return (
            <div key={activity.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="flex-shrink-0">
                <span className="text-lg">{getActivityIcon(activityType)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`font-medium ${getActivityColor(activityType)}`}>
                      {description}
                    </p>
                    {userName && (
                    <p className="text-sm text-slate-500">
                      by {userName}
                    </p>
                    )}
                    {activity.details?.changed_fields && Array.isArray(activity.details.changed_fields) && (
                      <div className="mt-1 space-y-1">
                        {activity.details.changed_fields.map((field, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="text-slate-400">{field.display_name || field.field_name}: </span>
                            {field.old_value && <span className="text-red-600 line-through">{field.old_value}</span>}
                            {field.old_value && field.new_value && <span className="mx-1">→</span>}
                            {field.new_value && <span className="text-green-600 font-medium">{field.new_value}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {activity.field_name && activity.old_value && activity.new_value && (
                      <div className="mt-1 text-sm">
                        <span className="text-red-600 line-through">{activity.old_value}</span>
                        <span className="mx-2">→</span>
                        <span className="text-green-600 font-medium">{activity.new_value}</span>
                      </div>
                    )}
                  </div>
                  <div 
                    className="text-xs text-slate-400 text-right cursor-help" 
                    title={getTimestampTooltip(timestamp)}
                  >
                    {formatTimestamp(timestamp)}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// OrderActions and ProcessorActions are now imported from ./components/orders/OrderActions

// Advanced Multi-Select Component
const AdvancedMultiSelect = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = "Select items...", 
  searchPlaceholder = "Search...",
  showSearch = true,
  showSelectAll = true,
  maxDisplayItems = 3,
  className = ""
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    onChange(options.map(option => option.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleToggleItem = (itemValue) => {
    const newValue = value.includes(itemValue)
      ? value.filter(v => v !== itemValue)
      : [...value, itemValue];
    onChange(newValue);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length <= maxDisplayItems) {
      return value.map(v => {
        const option = options.find(opt => opt.value === v);
        return option?.label || v;
      }).join(', ');
    }
    return `${value.length} selected`;
  };

  const getSelectedLabels = () => {
    return value.map(v => {
      const option = options.find(opt => opt.value === v);
      return option?.label || v;
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between text-left font-normal ${className}`}
          title={value.length > maxDisplayItems ? getSelectedLabels().join(', ') : ''}
        >
          <span className="truncate">{getDisplayText()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          {showSearch && (
            <CommandInput 
              placeholder={searchPlaceholder}
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
          )}
          <CommandList className="max-h-64">
            {showSelectAll && (
              <CommandGroup>
                <div className="flex gap-2 p-2 border-b">
                  <Button size="sm" variant="outline" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleClearAll}>
                    Clear All
                  </Button>
                </div>
              </CommandGroup>
            )}
            
            <CommandEmpty>No items found.</CommandEmpty>
            
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleToggleItem(option.value)}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      value.includes(option.value) ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Report Status Badge Utility

// Filter Chips Component
const FilterChips = ({ filters, filterOptions, onRemoveFilter, onClearAll }) => {
  const getChips = () => {
    const chips = [];

    // Date range chip (only show if not default)
    if (filters.dateRangePreset && filters.dateRangePreset !== 'last_30_days') {
      const presetLabels = {
        'last_7_days': 'Last 7 Days',
        'this_month': 'This Month',
        'last_month': 'Last Month',
        'custom': `${filters.customStartDate} to ${filters.customEndDate}`
      };
      chips.push({
        type: 'dateRange',
        label: presetLabels[filters.dateRangePreset] || filters.dateRangePreset,
        key: 'dateRange'
      });
    }

    // Status chips
    if (filters.statuses.length > 0) {
      if (filters.statuses.length <= 2) {
        filters.statuses.forEach(status => {
          chips.push({
            type: 'status',
            label: `Status: ${status}`,
            key: `status-${status}`,
            value: status
          });
        });
      } else {
        chips.push({
          type: 'status',
          label: `${filters.statuses.length} statuses`,
          key: 'status-multiple',
          tooltip: filters.statuses.join(', ')
        });
      }
    }

    // Client Product chips
    if (filters.clientProducts.length > 0) {
      if (filters.clientProducts.length <= 2) {
        filters.clientProducts.forEach(product => {
          chips.push({
            type: 'clientProduct',
            label: `Product: ${product}`,
            key: `product-${product}`,
            value: product
          });
        });
      } else {
        chips.push({
          type: 'clientProduct',
          label: `${filters.clientProducts.length} products`,
          key: 'product-multiple',
          tooltip: filters.clientProducts.join(', ')
        });
      }
    }

    // Assigned To chips
    if (filters.assignedUsers.length > 0) {
      const userLabels = filters.assignedUsers.map(userId => {
        const user = filterOptions.assignedUsers?.find(u => u.id === userId);
        return user?.name || userId;
      });

      if (userLabels.length <= 2) {
        userLabels.forEach((label, index) => {
          chips.push({
            type: 'assignedUser',
            label: `Assigned: ${label}`,
            key: `user-${filters.assignedUsers[index]}`,
            value: filters.assignedUsers[index]
          });
        });
      } else {
        chips.push({
          type: 'assignedUser',
          label: `${userLabels.length} users`,
          key: 'user-multiple',
          tooltip: userLabels.join(', ')
        });
      }
    }

    // Client Code chips
    if (filters.clientCodes.length > 0) {
      if (filters.clientCodes.length <= 2) {
        filters.clientCodes.forEach(code => {
          chips.push({
            type: 'clientCode',
            label: `Code: ${code}`,
            key: `code-${code}`,
            value: code
          });
        });
      } else {
        chips.push({
          type: 'clientCode',
          label: `${filters.clientCodes.length} codes`,
          key: 'code-multiple',
          tooltip: filters.clientCodes.join(', ')
        });
      }
    }

    return chips;
  };

  const chips = getChips();

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <span className="text-sm text-slate-600">Active filters:</span>
      {chips.map(chip => (
        <Badge 
          key={chip.key}
          variant="secondary" 
          className="flex items-center gap-1 cursor-pointer hover:bg-slate-200"
          title={chip.tooltip}
        >
          {chip.label}
          <button
            onClick={() => onRemoveFilter(chip.type, chip.value)}
            className="ml-1 hover:text-red-600"
            aria-label={`Remove ${chip.label} filter`}
          >
            ×
          </button>
        </Badge>
      ))}
      {chips.length > 0 && (
        <Button size="sm" variant="ghost" onClick={onClearAll} className="text-xs">
          Clear All
        </Button>
      )}
    </div>
  );
};

// ClientProductSelector is now imported from ./components/orders/ClientProductSelector

// Form Components
// Order Desk Edit Modal with Tabs (Order Details + Activity History)
const OrderDeskEditModal = ({ isOpen, onClose, order, onUpdate }) => {
  const [activityHistory, setActivityHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [fullOrderDetails, setFullOrderDetails] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const { user } = useAuth();
  // RBAC-PILOT-ORDERS-001: explicit matrix grant orders:update also unlocks field edits
  const { hasPermission: hasMatrixPermission } = usePermissions();
  
  useEffect(() => {
    if (isOpen && order) {
      fetchFullOrderDetails();
      fetchActivityHistory();
    }
  }, [isOpen, order]);

  const fetchFullOrderDetails = async () => {
    setLoadingOrder(true);
    try {
      const response = await axios.get(`${API}/orders/${order.id}/details`);
      // The endpoint returns { order, property, customer }
      const orderData = response.data.order || response.data;
      
      // Debug logging to verify data structure
      console.log('Order details fetched:', orderData);
      console.log('Client Code from API:', orderData.client_code);
      console.log('Product ID from API:', orderData.product_id);
      
      setFullOrderDetails(orderData);
    } catch (error) {
      console.error('Error fetching order details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Failed to load order details: ${error.response?.data?.detail || error.message}`);
      setFullOrderDetails(order); // Fallback to basic order data
    } finally {
      setLoadingOrder(false);
    }
  };

  const fetchActivityHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/orders/${order.id}/activity?page=1&limit=20`);
      setActivityHistory(response.data.events || response.data || []);
    } catch (error) {
      console.error('Error fetching activity history:', error);
      setActivityHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (orderData) => {
    const success = await onUpdate(order.id, orderData);
    if (success) {
      // Close modal immediately — the unlock is consumed on save,
      // so any post-save fetch would fail with 403.
      onClose();
    }
  };

  if (loadingOrder) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh]">
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p>Loading order details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const orderData = fullOrderDetails || order;

  // Check if order desk can edit: either unassigned or edit-unlocked
  const activeOrder = fullOrderDetails || order;
  const hasEditUnlock = activeOrder?.edit_unlocked_for === user?.id && activeOrder?.edit_unlock_expires_at && new Date(activeOrder.edit_unlock_expires_at) > new Date();

  // Feb 2026 — DELIVERY-EDIT-RBAC-001 — Role-aware edit gating.
  // Admin / Generic TL: always editable.
  // Tax Search TL: editable only when the order is in the Tax-team scope.
  // Title Search TL: editable only when the order is in the Title-team scope.
  // Order Desk: existing rule (unassigned OR has active edit_unlock).
  const userRoleNormalized = (user?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const orderIsTax = isTaxProduct(activeOrder);
  let canEdit = false;
  if (userRoleNormalized === 'admin' || userRoleNormalized === 'team_leader') {
    canEdit = true;
  } else if (userRoleNormalized === 'tax_search_team_leader') {
    canEdit = orderIsTax;
  } else if (userRoleNormalized === 'title_search_team_leader') {
    canEdit = !orderIsTax;
  } else if (userRoleNormalized === 'order_desk') {
    canEdit = hasEditUnlock || (!activeOrder?.assignee_id);
  } else if (hasMatrixPermission('orders', 'update')) {
    // RBAC-PILOT-ORDERS-001: matrix-granted editors (field edits only; status/assignment stay role-gated)
    canEdit = true;
  } else {
    // Processors and other roles fall through with no edit.
    canEdit = false;
  }

  // If order is assigned, show read-only view with message
  if (!canEdit) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>View Order - {orderData.order_number}</DialogTitle>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 text-amber-800">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium" data-testid="order-readonly-banner">
                {userRoleNormalized === 'tax_search_team_leader' || userRoleNormalized === 'title_search_team_leader'
                  ? 'This order is outside your team scope. You can view but not edit it.'
                  : 'This order is assigned and cannot be edited.'}
              </span>
            </div>
            {!(userRoleNormalized === 'tax_search_team_leader' || userRoleNormalized === 'title_search_team_leader') && (
              <p className="text-amber-700 text-sm mt-1">
                Assigned to: {orderData.assignee_name || 'Unknown'}
              </p>
            )}
          </div>
          
          <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="flex-shrink-0">
              <TabsTrigger value="details">Order Details</TabsTrigger>
              <TabsTrigger value="history">Activity History</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="flex-1 overflow-y-auto pr-2 mt-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><strong>Order Number:</strong> {orderData.order_number}</div>
                  <div><strong>Status:</strong> {orderData.status}</div>
                  <div><strong>Client Code:</strong> {orderData.client_code}</div>
                  <div><strong>Client Product:</strong> {orderData.client_product} <ContractCodeChip code={orderData.contract_code} testId="edit-readonly-contract-code" /></div>
                  <div><strong>Borrower:</strong> {orderData.borrower_name}</div>
                  <div><strong>Priority:</strong> {orderData.priority}</div>
                  <div className="col-span-2"><strong>Property Address:</strong> {orderData.property_address}</div>
                  <div><strong>City:</strong> {orderData.property_city}</div>
                  <div><strong>State:</strong> {orderData.property_state}</div>
                  <div><strong>County:</strong> {orderData.property_county}</div>
                  <div><strong>Zip:</strong> {orderData.property_zip_code}</div>
                </div>
                {orderData.notes && (
                  <div><strong>Notes:</strong> {orderData.notes}</div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="history" className="flex-1 overflow-y-auto pr-2 mt-4">
              {loadingHistory ? (
                <div className="p-8 text-center">Loading activity history...</div>
              ) : (
                <ActivityHistoryPanel history={activityHistory} />
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Edit Order - {orderData.order_number}</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex-shrink-0">
            <TabsTrigger value="details">Order Details</TabsTrigger>
            <TabsTrigger value="history">Activity History</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="flex-1 overflow-y-auto pr-2 mt-4">
            {(() => {
              const formInitialData = {
                product_id: orderData.product_id || '',
                client_code: orderData.client_code || '',
                client_product: orderData.client_product || '',
                contract_code: orderData.contract_code || '',
                borrower_name: orderData.borrower_name || orderData.customer || '',
                seller_name: orderData.seller_name || '',
                property_address: orderData.property_address || orderData.property || '',
                property_city: orderData.property_city || '',
                property_state: orderData.property_state || '',
                property_zip_code: orderData.property_zip_code || orderData.property_zip || '',
                property_county: orderData.property_county || orderData.county || '',
                priority: orderData.priority || 'normal',
                priority_notes: orderData.priority_notes || '',
                notes: orderData.notes || '',
                parcel: orderData.parcel || ''
              };
              
              // Debug: Log the initial data being passed to form
              console.log('Form initialData being passed:', formInitialData);
              
              return (
                <CreateOrderForm 
                  onSubmit={handleSubmit}
                  onCancel={onClose}
                  initialData={formInitialData}
                  productWorkflowType={orderData.workflow_type || null}
                  isEditMode={true}
                />
              );
            })()}
          </TabsContent>

          <TabsContent value="history" className="flex-1 overflow-y-auto pr-2 mt-4">
            {loadingHistory ? (
              <div className="p-8 text-center">Loading activity history...</div>
            ) : (
              <ActivityHistoryPanel history={activityHistory} />
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Export OrderDeskEditModal for use in OrdersManagement
export { OrderDeskEditModal };

// CreateOrderForm is now imported from ./components/forms/CreateOrderForm


// CreateCustomerForm is now imported from ./components/forms/CreateCustomerForm

// FavoritesManagement is now imported from ./components/admin/FavoritesManagement

// ProcessorKPICards is now imported from ./components/processor/ProcessorKPICards

// Processor Orders Component
// Helper function to check if user is any type of processor
// Using imported function from orderHelpers
const isProcessorRole = importedIsProcessorRole;

// StatusBadge, normalizeStatusForOrderDesk, and PriorityIndicator are now imported from their respective component files

// Shared Date Formatting Utility
const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    
    // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional legacy unused helper
    return date.toLocaleDateString('en-US', { 
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    return '—';
  }
};

// Assignment Button Component for Team Leaders
const AssignOrderButton = ({ order, onAssignSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [processors, setProcessors] = useState([]);
  const [selectedProcessor, setSelectedProcessor] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Fetch processors when dropdown opens
  useEffect(() => {
    const fetchProcessors = async () => {
      if (isOpen && processors.length === 0) {
        try {
          const response = await axios.get(`${API}/users/processors`);
          setProcessors(response.data || []);
        } catch (error) {
          console.error('Failed to fetch processors:', error);
          toast.error('Failed to load processors');
        }
      }
    };

    fetchProcessors();
  }, [isOpen]);

  const handleAssign = async () => {
    if (!selectedProcessor) {
      toast.error('Please select a processor');
      return;
    }

    setIsAssigning(true);
    try {
      const response = await axios.post(`${API}/orders/${order.id}/assign`, {
        assignee_id: selectedProcessor
      });

      toast.success(`Order assigned successfully to ${processors.find(p => p.id === selectedProcessor)?.full_name || 'processor'}`);
      setIsOpen(false);
      setSelectedProcessor('');
      
      // Refresh the orders list
      if (onAssignSuccess) {
        onAssignSuccess();
      }
    } catch (error) {
      console.error('Assignment failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to assign order';
      toast.error(errorMessage);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          variant="outline" 
          className="text-xs px-2 py-1 h-7"
        >
          Assign
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Order</DialogTitle>
          <DialogDescription>
            Assign order {order.orderNumber} to a processor.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="processor">Select Processor</Label>
            <Select value={selectedProcessor} onValueChange={setSelectedProcessor}>
              <SelectTrigger>
                <SelectValue placeholder="Select a processor" />
              </SelectTrigger>
              <SelectContent>
                {processors.map((processor) => (
                  <SelectItem key={processor.id} value={processor.id}>
                    {processor.full_name || processor.email} ({processor.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setIsOpen(false)}
            disabled={isAssigning}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAssign} 
            disabled={!selectedProcessor || isAssigning}
          >
            {isAssigning ? 'Assigning...' : 'Assign Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ProcessorOrders component has been extracted to /app/frontend/src/pages/ProcessorOrders.jsx


// Blocked Route Component for Order Desk
const BlockedForOrderDesk = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (user?.role === 'order_desk') {
      toast.error("This page isn't available for your role.", {
        description: "You've been redirected to the dashboard."
      });
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);
  
  // For non-order-desk users, show a placeholder (would be replaced with actual components)
  if (user?.role !== 'order_desk') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Profile & Settings</CardTitle>
            <CardDescription>This page is under development</CardDescription>
          </CardHeader>
          <CardContent>
            <p>Profile and Settings functionality will be available soon.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return null;
};


// ClientCodeManagement is now imported from ./components/admin/ClientCodeManagement

// Glassmorphism toast functions are now imported from ./utils/glassmorphismToasts

// Main Dashboard Component
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardStatsRef = useRef(null);
  const [orderTypeFilterValue, setOrderTypeFilterValue] = useState('all'); // For Admin order type filtering
  const [unassignedOnlyFilter, setUnassignedOnlyFilter] = useState(false); // For Admin/Team Leader unassigned filter
  const [yearFilterValue, setYearFilterValue] = useState(null); // May 5, 2026 — mirror OrdersManagement year for stats card ↔ table parity
  // RBAC-PILOT-ORDERS-001: matrix grants may open /dashboard/orders for JR
  const { hasPermission: hasMatrixPermission, loading: matrixLoading } = usePermissions();
  const isJR = isJurisdictionRequestor(user);
  const jrAccess = isJR ? jrOrdersAccess(location.pathname, hasMatrixPermission, matrixLoading) : null;
  // OD-PARITY-001: native order_desk OR Order Desk workspace + orders:view → Order Desk look
  const { isOrderDeskView } = useWorkspacePresentation(user);
  // RBAC-WS-SEARCH-001/002: matrix grant lets a non-processor role (QC Specialist) enter a
  // processor workspace (Title Search or Tax, per qc_product_scope — /auth/me permissions are
  // already scope-filtered by the backend). Backend RBAC stays authoritative.
  const canEnterTitleSearchWs = hasMatrixPermission('title_search', 'access') || hasMatrixPermission('tax_search', 'access');
  const titleSearchWsPath = location.pathname === '/dashboard/processor-orders';
  
  // ============================================================================
  // ROUTE-BASED NAVIGATION (Replaces activeView state)
  // Maps URL paths to view names for backward compatibility
  // ============================================================================
  
  // BM-007: maps moved to lib/dashboardViews.js (shared with AppHeader)
  const routeToViewMap = ROUTE_TO_VIEW;
  const viewToRouteMap = VIEW_TO_ROUTE;
  
  // Get current view from URL path
  const getCurrentView = () => {
    const path = location.pathname;
    return routeToViewMap[path] || 'orders';
  };
  
  // Current active view derived from route
  const activeView = getCurrentView();
  
  // Navigation helper - replaces setActiveView
  const setActiveView = (view) => {
    const route = viewToRouteMap[view] || '/dashboard';
    navigate(route);
  };
  
  // BM-007: getDefaultRoute / isITRole / isQCSpecialistRole / isAccountingRole imported from lib/dashboardViews.js
  
  // Redirect to role-appropriate default if at bare /dashboard and role needs different default
  useEffect(() => {
    // Defense-in-depth: jurisdiction_requestor must NEVER land on any
    // /dashboard route, even if the canonical `role` field is missing
    // from the cached user record.
    // May 17, 2026 — single exception: `/dashboard/workspace` is allowed
    // for jurisdiction_requestor (and all roles) per the My Workspace
    // RBAC update. All other operational `/dashboard/*` paths remain
    // blocked so the jurisdiction portal stays the canonical landing.
    if (
      isJR
      && location.pathname.startsWith('/dashboard')
      && location.pathname !== '/dashboard/workspace'
      && jrAccess === 'redirect'
    ) {
      navigate('/admin/my-jurisdiction-dashboard', { replace: true });
      return;
    }

    if (location.pathname === '/dashboard') {
      const defaultRoute = getDefaultRoute(user?.role);
      if (defaultRoute !== '/dashboard') {
        navigate(defaultRoute, { replace: true });
      }
    }
    
    // Block IT roles from accessing production dashboard routes
    // May 17, 2026 — Single exception: `/dashboard/workspace` (My Workspace)
    // is allowed for IT roles per RBAC update. All other operational
    // dashboards remain blocked.
    if (isITRole(user?.role)) {
      const productionRoutes = ['/dashboard', '/dashboard/orders', '/dashboard/reports', 
                               '/dashboard/processor-orders', '/dashboard/favorites'];
      if (productionRoutes.includes(location.pathname)) {
        const defaultRoute = getDefaultRoute(user?.role);
        navigate(defaultRoute, { replace: true });
      }
    }
    
    // Redirect QC Specialists to QC Dashboard if they try to access processor dashboard
    // RBAC-WS-SEARCH-001: /dashboard/processor-orders stays open while the matrix grant
    // title_search:access is held (or still loading) — everything else redirects as before.
    if (isQCSpecialistRole(user?.role)) {
      const processorRoutes = ['/dashboard', '/dashboard/orders', '/dashboard/reports', 
                               '/dashboard/processor-orders', '/dashboard/favorites'];
      const titleSearchWsAllowed = titleSearchWsPath && (matrixLoading || canEnterTitleSearchWs);
      if (processorRoutes.includes(location.pathname) && !titleSearchWsAllowed) {
        navigate('/qc-dashboard', { replace: true });
      }
    }
    
    // Accounting persona has no operational dashboard access — always
    // route them to the Billing Dashboard (which now lives inside the
    // global Dashboard shell, so they still get the unified header).
    if (isAccountingRole(user?.role)) {
      const blockedRoutes = ['/dashboard', '/dashboard/orders', '/dashboard/reports',
                             '/dashboard/processor-orders', '/dashboard/favorites'];
      if (blockedRoutes.includes(location.pathname)) {
        navigate('/admin/billing', { replace: true });
      }
    }
  }, [location.pathname, user?.role, navigate, isJR, jrAccess, titleSearchWsPath, matrixLoading, canEnterTitleSearchWs]);
  
  // Show glassmorphism welcome banner once per login session
  useEffect(() => {
    const shouldShowWelcome = sessionStorage.getItem('showWelcomeBanner');
    if (shouldShowWelcome === 'true' && user) {
      // Small delay to ensure smooth transition from login
      setTimeout(() => {
        showGlassmorphismWelcome(user.full_name || user.email, user.role);
      }, 500);
      // Clear the flag so it doesn't show again on refresh/navigation
      sessionStorage.removeItem('showWelcomeBanner');
    }
  }, [user]);

  // QC Specialists should never render the main Dashboard shell when it
  // would expose order-desk widgets — they are redirected to /qc-dashboard.
  // Return a minimal loading state to prevent shared components (BreakButton,
  // DashboardStats, etc.) from firing unauthorized API calls before the
  // redirect useEffect triggers.
  //
  // Feb 2026 — QCC-WORKSPACE-FIX-001: `/dashboard/workspace` is the
  // single exception (mirrors the jurisdiction_requestor carve-out below).
  // QC users have a "My Workspace" button on the Quality Command Center
  // header (QCDashboard.jsx) which deep-links here, and were previously
  // stuck on a perpetual spinner because this guard returned before the
  // Dashboard shell could render MyWorkspace.
  if (
    isQCSpecialistRole(user?.role)
    && location.pathname !== '/dashboard/workspace'
    && !(titleSearchWsPath && (matrixLoading || canEnterTitleSearchWs))
  ) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-6 h-6 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Feb 25, 2026 — defense-in-depth: jurisdiction_requestor must never
  // render the Dashboard shell, even briefly. The ProtectedRoute and
  // useEffect redirects above should already handle this, but render
  // a benign loading placeholder for the single tick before navigation
  // settles, so the orders queue NEVER paints for this persona.
  // May 17, 2026 — exception: `/dashboard/workspace` is allowed for
  // jurisdiction_requestor (My Workspace RBAC). Let the Dashboard shell
  // render so the workspace view paints.
  if (isJR && location.pathname !== '/dashboard/workspace' && jrAccess !== 'allow') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="jr-redirect-loader">
        <div className="text-center">
          <div className="w-6 h-6 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-600">Loading your jurisdiction dashboard…</p>
        </div>
      </div>
    );
  }

  // OPS-PREMIUM-UI-001: premium animated surface for the Operations dashboard
  // (Admin / TL presentation only — Order Desk, Typing and IT views unchanged).
  const showOpsPremium = activeView === 'orders' && !isITRole(user?.role) && !isOrderDeskView && user?.role !== 'typing_processor';

  return (
    <div className={showOpsPremium ? 'min-h-screen bg-slate-50 ops-premium' : 'min-h-screen bg-slate-50'}>
      {/* BM-007: standard TitleFlow header lives in components/shared/AppHeader.jsx (moved, not copied). */}
      <AppHeader activeView={activeView} onSelectView={setActiveView} />
      {showOpsPremium && <OpsHeroBanner />}

      {/* Main Content */}
      {activeView === 'billing' ? (
        // Billing has its own self-contained layout (sticky sub-header,
        // max-w-1440 container). Render OUTSIDE the standard <main>
        // padding so we don't double-up on horizontal/vertical gutters.
        <BillingDashboard />
      ) : (
      <main className={`mx-auto px-6 ${activeView === 'my-workspace' ? 'py-4' : 'py-8'}`} style={{ maxWidth: '1440px' }}>
        {/* IT Roles: Show message if they try to access production dashboard */}
        {activeView === 'orders' && isITRole(user?.role) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <Shield className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-amber-800 mb-2">Access Restricted</h2>
            <p className="text-amber-700 mb-4">
              The Production Dashboard is restricted to operations roles only.
            </p>
            <button 
              onClick={() => navigate(getDefaultRoute(user?.role))}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              Go to {user?.role === 'it_manager' ? 'User Management' : 'My Workspace'}
            </button>
          </div>
        )}
        
        {/* Production Dashboard - Only for non-IT roles */}
        {activeView === 'orders' && !isITRole(user?.role) && (
          <>
            {/* OD-SIMPLIFY-001: the Order Desk presentation (native + workspace) has no KPI /
                product-breakdown block, so DashboardStats is not mounted and GET /dashboard/stats
                is never requested for it. Other operational roles are unchanged. */}
            {!isOrderDeskView && (
              <DashboardStats ref={dashboardStatsRef} orderTypeFilter={orderTypeFilterValue} unassignedOnly={unassignedOnlyFilter} year={yearFilterValue} user={user} />
            )}
            
            {/* Edit Requests Indicator - Small card linking to full page */}
            <EditRequestsIndicator currentUser={user} />
            
            <OrdersManagement 
              activeView={activeView} 
              setActiveView={setActiveView}
              refreshStats={() => dashboardStatsRef.current?.refreshStats()}
              onOrderTypeFilterChange={setOrderTypeFilterValue}
              onUnassignedOnlyChange={setUnassignedOnlyFilter}
              onYearChange={setYearFilterValue}
            />
          </>
        )}
        
        {activeView === 'favorites' && isOrderDeskView && (
          <FavoritesManagement />
        )}
        
        {activeView === 'processor-orders' && (isProcessorRole(user?.role) || canEnterTitleSearchWs) && (
          <ProcessorOrders />
        )}

        {/* TYPING-001 Phase 2b — Typing queue inside the unified Dashboard
            shell (same header: Break / Downtime / notifications / profile).
            'orders' alias keeps a bare /dashboard visit from rendering blank. */}
        {(activeView === 'typing' || (activeView === 'orders' && user?.role === 'typing_processor')) &&
          (holdsTypingRole(user) || user?.role === 'admin') && (
          <TypingDashboard embedded />
        )}

        {/* TYPING-SCOPE-001: any other role landing on /dashboard/typing gets
            sent to their own dashboard instead of a blank shell */}
        {activeView === 'typing' && user && !holdsTypingRole(user) && user.role !== 'admin' && (
          <Navigate to={getRoleDashboardPath(user)} replace />
        )}
        
        {activeView === 'reports' && (
          <ReportingDashboard user={user} setActiveView={setActiveView} />
        )}

        {/* Layer 2: My Workspace - Personal HR & Support Hub */}
        {activeView === 'my-workspace' && (
          <MyWorkspace 
            currentUser={user} 
            onNavigateBack={() => setActiveView('orders')}
          />
        )}

        {/* Layer 2: Team Management - Team Leader/Admin Only */}
        {activeView === 'team-management' && (
          <TeamManagement 
            currentUser={user} 
            onNavigateBack={() => setActiveView('orders')}
          />
        )}

        {/* Layer 2: Edit Requests - Team Leader/Admin Only */}
        {activeView === 'edit-requests' && (
          <EditRequestsPage 
            currentUser={user}
          />
        )}

        {/* Legacy routes - redirect to new structure */}
        {activeView === 'leave-management' && (
          <MyWorkspace 
            currentUser={user} 
            onNavigateBack={() => setActiveView('orders')}
          />
        )}

        {activeView === 'my-attendance' && (
          <MyWorkspace 
            currentUser={user} 
            onNavigateBack={() => setActiveView('orders')}
          />
        )}
      </main>
      )}
      
      {/* Work Proof Prompt - Compliance-safe activity check */}
      <WorkProofPrompt />
    </div>
  );
};

// TITLE-QC-DOC-EXPENSE-001 — mirrors backend routes/pricing._paid_site_fee_order_workflow (client_product keyword classifier).
const isTitleWorkflowOrder = (o) => !!o && !/tax|mls|utility|municipal|hoa/.test(String(o.client_product || '').toLowerCase());

// Order Details Layout with Tabs
const OrderDetailsLayout = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isOrderDeskView, isProcessorWorkspaceView: isTitleSearchView, presentation, activeWorkspace } = useWorkspacePresentation(user); // OD-PARITY-001 / RBAC-WS-SEARCH-001/002
  // DUAL-ROLE-OVERVIEW-001 (Sep 2026): order-detail gates follow the ACTIVE
  // workspace's effective role (same source the dashboard uses), not the raw
  // primary role. QC workspace → qc_specialist; Title Search workspace → search_processor.
  const effectiveRole = isTitleSearchView ? (presentation || user?.role) : user?.role;
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  // Mark Delivered modal — Feb 25, 2026 P0
  const [showMarkDeliveredDialog, setShowMarkDeliveredDialog] = useState(false);
  const [markDeliveredNote, setMarkDeliveredNote] = useState('');
  const [markDeliveredSubmitting, setMarkDeliveredSubmitting] = useState(false);
  const [markDeliveredError, setMarkDeliveredError] = useState(null);
  // Notification deep-link highlight — Feb 25, 2026.
  // When users land here via a delivery-exception notification
  // (?from=notification on the URL), scroll the "Mark Delivered" button
  // into view and pulse-highlight it for 3s so the next action is
  // unmistakable. Idempotent: triggers once per orderData fetch.
  const [highlightMarkDelivered, setHighlightMarkDelivered] = useState(false);
  // One-shot guard so the auto-mark-read for deleted-order-from-notification
  // only fires once per error render (refs persist across re-renders, state
  // mutations on the error object don't).
  const _staleNotifClearedRef = React.useRef(false);
  useEffect(() => {
    if (!orderData) return;
    const params = new URLSearchParams(location.search);
    if (params.get('from') !== 'notification') return;
    // Only highlight when the action is actually relevant.
    const isExceptionState =
      (orderData.status === 'complete' || orderData.status === 'completed') &&
      !['sent', 'delivered', 'delivered_manually'].includes(orderData.delivery_status);
    if (!isExceptionState) return;
    // Defer to the next paint so the button has rendered.
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="mark-delivered-btn"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightMarkDelivered(true);
        setTimeout(() => setHighlightMarkDelivered(false), 3500);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [orderData, location.search]);

  const handleMarkDelivered = async () => {
    setMarkDeliveredSubmitting(true);
    setMarkDeliveredError(null);
    try {
      const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const r = await fetch(`${apiBase}/orders/${orderData.id}/mark-delivered`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ note: markDeliveredNote.trim() || null }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.detail || `Request failed (${r.status})`);
      }
      const body = await r.json();
      setOrderData((prev) => ({
        ...prev,
        delivery_status: body.delivery_status,
        delivery_marked_delivered_at: body.marked_at,
        delivery_marked_delivered_by: body.marked_by,
        delivery_marked_delivered_note: markDeliveredNote.trim() || prev?.delivery_marked_delivered_note,
      }));
      setShowMarkDeliveredDialog(false);
      setMarkDeliveredNote('');
    } catch (e) {
      setMarkDeliveredError(e.message || 'Failed to mark as delivered.');
    } finally {
      setMarkDeliveredSubmitting(false);
    }
  };

  // Check if user can reassign orders (Team Leader or Admin)
  const canReassign = user?.role === 'admin' || 
                      user?.role === 'tax_search_team_leader' || 
                      user?.role === 'title_search_team_leader' ||
                      user?.role === 'team_leader';

  // P0: Check if order is unassigned and user is in an enforced role
  const isEnforcedRole = ['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader',
    'processor', 'title_search_processor', 'search_processor'].includes(user?.role);
  // MLS-ASSIGN-3B: dual MLS orders keep order.assignee_id = null by design —
  // ownership lives on components.tax/mls. The parent assignment gate must
  // never block dual orders (component-level gates are enforced by the
  // backend and the component workspace controls).
  const isDualMlsOrder = !!(orderData?.components?.tax && orderData?.components?.mls);
  const isDualFullyUnassigned = isDualMlsOrder &&
    !orderData.components.tax?.assignee_id && !orderData.components.mls?.assignee_id;
  const isOrderUnassigned = orderData && !isDualMlsOrder && !orderData.assignee_id;
  const isProcessingBlocked = isEnforcedRole && isOrderUnassigned && 
    !['complete', 'cancelled'].includes(orderData?.status);

  // Quick "Assign to Me" handler
  const handleAssignToMe = async () => {
    try {
      await axios.patch(`${API}/orders/${orderId}/assignee`, { assignee_id: user.id });
      // Refresh order data
      const response = await axios.get(`${API}/orders/${orderId}/details`);
      const combinedData = {
        ...response.data.order,
        property: response.data.property,
        customer: response.data.customer
      };
      setOrderData(combinedData);
    } catch (error) {
      console.error('Assign to me failed:', error);
      alert(error.response?.data?.detail || 'Failed to assign order');
    }
  };

  // Extract current tab from URL
  const currentPath = location.pathname;
  const getCurrentTab = () => {
    if (currentPath.includes('/summary')) return 'summary';
    if (currentPath.includes('/overview')) return 'overview';
    if (currentPath.includes('/submissions')) return 'submissions';
    if (currentPath.includes('/fulfillment')) return 'fulfillment';
    if (currentPath.includes('/activity')) return 'activity';
    if (currentPath.includes('/files')) return 'files';
    
    // Feb 25, 2026: every role lands on the new billing-FREE Summary
    // tab by default. Admin/Accounting still see Overview as a separate
    // tab; everyone else (Processors, TLs, QC, Order Desk) lands on
    // Summary first and can navigate to Fulfillment/Activity/Files
    // explicitly. Replaces the old "default to Fulfillment" override
    // which jumped processors straight into work without context.
    return 'summary';
  };
  const [activeTab, setActiveTab] = useState(getCurrentTab());

  // Update active tab when URL changes
  useEffect(() => {
    const newTab = getCurrentTab();
    setActiveTab(newTab);
    
    // Redirect non-admin/non-accounting/non-tax-lead users away from Overview tab if they try to access it directly
    // Overview tab contains confidential billing information — only Admin, Accounting, and
    // Tax Search Team Leader (May 17, 2026 RBAC) can access. Tax Lead's view inside the
    // Overview tab is structurally narrowed to the OperationalFeesPanel only — the
    // pricing internals block (Base Price, Margin, Internal Cost, Override, Recalculate)
    // is still gated to admin/accounting inside OrderTabs.jsx.
    // Feb 25, 2026: redirect target switched from /fulfillment to /summary
    // so the user lands on a context page first (per Option A spec).
    // TITLE-QC-DOC-EXPENSE-001: QC Specialist with TITLE scope → Overview on Title Search orders only.
    // Decided once the order is loaded (the guard re-runs when orderData arrives) so the first render
    // never bounces a legitimate QC user back to Summary.
    const qcTitleScope = effectiveRole === 'qc_specialist'
      && (user?.qc_product_scope || []).map((s) => String(s).toLowerCase()).includes('title');
    if (qcTitleScope && newTab === 'overview' && !orderData) return;
    const canAccessOverview = (
      effectiveRole === 'admin'
      || effectiveRole === 'accounting'
      || effectiveRole === 'tax_search_team_leader'
      // TITLE-OPS-FEES-001 (Aug 2026): Title Search roles record operational
      // costs (paid-site fees) via the Overview tab's OperationalFeesPanel.
      || effectiveRole === 'title_search_team_leader'
      || effectiveRole === 'title_search_processor'
      || effectiveRole === 'search_processor'
      // TITLE-FEE-TYPES-002 (Jun 2026): Typing views Operational Fees read-only.
      || effectiveRole === 'typing_processor'
      || (qcTitleScope && isTitleWorkflowOrder(orderData))
    );
    // Submissions stays limited to the original Overview roles — Title Search
    // roles only gained Overview (TITLE-OPS-FEES-001), not Submissions.
    const canAccessSubmissions = (
      effectiveRole === 'admin'
      || effectiveRole === 'accounting'
      || effectiveRole === 'tax_search_team_leader'
    );
    if ((!canAccessOverview && newTab === 'overview') || (!canAccessSubmissions && newTab === 'submissions')) {
      navigate(`/orders/${orderId}/summary${location.search}`, { replace: true });
      return;
    }
  }, [location.pathname, location.search, effectiveRole, orderId, navigate, orderData, user]);
  
  // Listen for openFilesTab events from Smart Evidence Prompt
  // When user clicks "Upload Call Recording" or similar buttons in TaxCertificatePanel,
  // this switches to the Files tab so the FilesTab component can open the upload modal
  useEffect(() => {
    const handleOpenFilesTab = (event) => {
      const { category, isRedispatch } = event.detail || {};
      
      // Skip if this is a re-dispatched event (to prevent infinite loop)
      if (isRedispatch) {
        return;
      }
      
      console.log('[OrderDetail] Received openFilesTab event, switching to Files tab with category:', category);
      // Switch to the Files tab (preserve ?from= origin context)
      setActiveTab('files');
      navigate(`/orders/${orderId}/files${window.location.search}`, { replace: true });
      
      // Re-dispatch the event after a small delay to allow FilesTab to mount and register its listener
      // Mark it as a re-dispatch so we don't process it again
      if (category) {
        setTimeout(() => {
          console.log('[OrderDetail] Re-dispatching openFilesTab event for FilesTab');
          window.dispatchEvent(new CustomEvent('openFilesTab', { detail: { category, isRedispatch: true } }));
        }, 100);
      }
    };
    
    window.addEventListener('openFilesTab', handleOpenFilesTab);
    
    return () => {
      window.removeEventListener('openFilesTab', handleOpenFilesTab);
    };
  }, [orderId, navigate]);

  // Fetch order details
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API}/orders/${orderId}/details`);
        const combinedData = {
          ...response.data.order,
          property: response.data.property,
          customer: response.data.customer
        };
        
        // Log parcel data for debugging Mail-Away modal issues
        console.log('📦 Order details loaded:', {
          order_id: response.data.order.id,
          order_number: response.data.order.order_number,
          has_parcels: 'parcels' in response.data.order,
          parcels_count: response.data.order.parcels?.length || 0,
          parcels_data: response.data.order.parcels
        });
        
        setOrderData(combinedData);
        
        // Check if this is a Tax Search/Update Tax Search order
        // Property Information step removed - users now access Fulfillment directly
        
      } catch (error) {
        console.error('Error fetching order details:', error);
        if (error.response?.status === 404) {
          setError({ type: 'not_found', message: 'Order not found' });
        } else if (error.response?.status === 403) {
          setError({ type: 'access_denied', message: 'You do not have permission to view this order' });
        } else {
          setError({ type: 'general', message: 'Failed to load order details' });
        }
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  // MAILAWAY-RECOVERY-002: silent authoritative refetch (no loading flip / remount)
  const refreshOrderData = useCallback(async () => {
    if (!orderId) return;
    try {
      const response = await axios.get(`${API}/orders/${orderId}/details`);
      setOrderData(prev => ({ ...prev, ...response.data.order, property: response.data.property, customer: response.data.customer }));
    } catch (error) {
      console.warn('Order refresh failed (non-fatal):', error);
    }
  }, [orderId]);

  const handleBack = () => {
    // Honor explicit ?from= query param first (e.g. Billing Dashboard
    // opens orders in a new tab with ?from=billing). This makes the
    // back-link survive new-tab opens where there is no history stack.
    const fromParam = new URLSearchParams(location.search).get('from');
    if (fromParam === 'billing') {
      navigate('/admin/billing');
      return;
    }
    if (fromParam === 'quality-records') {
      navigate('/my-quality-records');
      return;
    }
    if (user?.role === 'accounting') {
      // Accounting users have no processor-orders context — always
      // return to the Billing Dashboard.
      navigate('/admin/billing');
      return;
    }
    if (isTitleSearchView) {
      // RBAC-WS-SEARCH-001 / OD-DUAL-ROLE-002: opened from the active Title Search / processor workspace —
      // back-nav follows the selected workspace, not the primary role.
      navigate('/dashboard/processor-orders');
    } else if (isOrderDeskView && user?.role !== 'admin') {
      navigate(activeWorkspace?.home || '/dashboard');
    } else if (user?.role === 'qc_specialist') {
      navigate('/qc-dashboard');
    } else if (user?.role === 'typing_processor') {
      // Typists return to the Typing Queue, not the generic dashboard.
      navigate('/dashboard/typing');
    } else if (isJurisdictionRequestor(user)) {
      // OD-PARITY-001: matrix-granted Order Desk workspace lives at /dashboard/orders
      navigate('/dashboard/orders');
    } else {
      navigate('/dashboard');
    }
  };

  // Compute back-link label based on origin context.
  // Priority: explicit ?from= param > user role default > generic fallback.
  const backLabel = (() => {
    const fromParam = new URLSearchParams(location.search).get('from');
    if (fromParam === 'billing') return '← Billing';
    if (fromParam === 'quality-records') return '← My Quality Records';
    if (user?.role === 'accounting') return '← Billing';
    if (user?.role === 'qc_specialist' && isTitleSearchView) return '← My Orders';
    if (isTitleSearchView) return '← Processor Orders';
    if (isOrderDeskView && user?.role !== 'admin') return '← Orders';
    if (user?.role === 'qc_specialist') return '← QC Dashboard';
    if (user?.role === 'typing_processor') return '← Typing Queue';
    return '← Processor Orders';
  })();

  const handleRetry = () => {
    window.location.reload();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Preserve origin context (?from=quality-records / ?from=billing) across
    // tab switches so handleBack/backLabel keep working after e.g. Activity.
    navigate(`/orders/${orderId}/${tab}${location.search}`);
  };

  // Keyboard navigation for tabs
  const handleTabKeyDown = (event, tabId) => {
    const currentIndex = tabs.findIndex(tab => tab.id === tabId);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleTabChange(tabId);
        return;
      default:
        return;
    }

    const nextTab = tabs[nextIndex];
    if (nextTab) {
      document.getElementById(`${nextTab.id}-tab`)?.focus();
    }
  };

  // Tab configuration - conditional based on user role
  // Feb 25, 2026: every role gets a "Summary" tab as the first/default
  // landing tab. Admin/Accounting additionally see "Overview" (which
  // contains the confidential Billing Panel). All other roles hide
  // Overview completely.
  const tabs = (() => {
    const summaryTab = { id: 'summary', label: 'Summary', path: `/orders/${orderId}/summary` };
    const baseTabs = [
      summaryTab,
      { id: 'fulfillment', label: 'Fulfillment', path: `/orders/${orderId}/fulfillment` },
      { id: 'activity',    label: 'Activity',    path: `/orders/${orderId}/activity` },
      { id: 'files',       label: 'Files',       path: `/orders/${orderId}/files` },
    ];

    // Admin / Accounting / Tax Search Team Leader → splice Overview right after Summary.
    // May 17, 2026 — Tax Search Team Leader now gets the Overview tab so
    // they can access the restricted Operational Fees panel. The full
    // Billing Information block inside OverviewTab is still gated to
    // admin/accounting only (OrderTabs.jsx:265), so Tax Lead sees ONLY
    // the OperationalFeesPanel — no Base Price / Margin / Internal Cost
    // / Override / Recalculate / Client Pricing.
    if (
      effectiveRole === 'admin'
      || effectiveRole === 'accounting'
      || effectiveRole === 'tax_search_team_leader'
      // TITLE-OPS-FEES-001 (Aug 2026): Title Search roles get Overview for
      // the OperationalFeesPanel only — billing internals stay admin/accounting.
      || effectiveRole === 'title_search_team_leader'
      || effectiveRole === 'title_search_processor'
      || effectiveRole === 'search_processor'
      // TITLE-FEE-TYPES-002 (Jun 2026): Typing gets Overview for READ-ONLY
      // Operational Fees visibility once the order reaches Typing.
      || effectiveRole === 'typing_processor'
      // TITLE-QC-DOC-EXPENSE-001 (Oct 2026): QC Specialist with TITLE scope gets
      // Overview on Title Search orders ONLY, for the Operational Fees panel
      // (document-purchase expenses). Everything else in Overview is read-only
      // for QC; Billing Information stays admin/accounting.
      || (effectiveRole === 'qc_specialist'
          && (user?.qc_product_scope || []).map((s) => String(s).toLowerCase()).includes('title')
          && isTitleWorkflowOrder(orderData))
    ) {
      return [
        summaryTab,
        { id: 'overview', label: 'Overview', path: `/orders/${orderId}/overview` },
        ...baseTabs.slice(1),
      ];
    }

    // For ALL other roles (Processors, Team Leaders, QC Specialists, Order Desk, etc.)
    // Overview hidden — Summary is the safe context page.
    return baseTabs;
  })();

  // Loading state with skeletons - rounded grey bars
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
          <div className="px-6 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleBack} className="text-blue-600 hover:text-blue-700">
                {backLabel}
              </Button>
              <div className="h-6 bg-slate-200 rounded-full w-48 animate-pulse"></div>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
                <div className="h-3 bg-slate-200 rounded-full w-16 mb-2 animate-pulse"></div>
                <div className="h-4 bg-slate-200 rounded-full w-20 animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
                <div className="h-5 bg-slate-200 rounded-full w-32 mb-4 animate-pulse"></div>
                <div className="space-y-3">
                  <div className="h-3 bg-slate-200 rounded-full animate-pulse"></div>
                  <div className="h-3 bg-slate-200 rounded-full w-3/4 animate-pulse"></div>
                  <div className="h-3 bg-slate-200 rounded-full w-1/2 animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state with clean blue-tinted placeholder
  if (error) {
    // Feb 25, 2026: friendlier copy when the order was deleted but a
    // delivery-exception notification still pointed here. Auto-clear
    // the matching unread notification so the bell stops surfacing it.
    const fromNotification =
      new URLSearchParams(location.search).get('from') === 'notification';
    const isDeletedFromNotification =
      fromNotification && error.type === 'not_found';
    if (isDeletedFromNotification && orderId && !_staleNotifClearedRef.current) {
      _staleNotifClearedRef.current = true;
      (async () => {
        try {
          // Find any unread notification for this orderId and mark read.
          const apiBase = process.env.REACT_APP_BACKEND_URL;
          const tok = localStorage.getItem('token');
          const resp = await fetch(`${apiBase}/api/notifications`, {
            headers: { Authorization: `Bearer ${tok}` },
          });
          if (resp.ok) {
            const items = await resp.json();
            const matches = (items || []).filter(
              (n) => n.order_id === orderId && !n.is_read
            );
            for (const n of matches) {
              await fetch(`${apiBase}/api/notifications/${n.id}/mark-read`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok}` },
              });
            }
          }
        } catch (_) {
          /* best-effort — never block the UI */
        }
      })();
    }

    const headline = isDeletedFromNotification
      ? 'Order no longer available'
      : error.type === 'not_found'
        ? 'Order Not Found'
        : error.type === 'access_denied'
          ? 'Access Denied'
          : 'Error Loading Order';
    const bodyMsg = isDeletedFromNotification
      ? 'This order has been deleted or merged. The related notification has been dismissed automatically.'
      : error.message;

    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
          <div className="px-6 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleBack} className="text-blue-600 hover:text-blue-700">
                {backLabel}
              </Button>
              <span className="text-lg font-semibold text-slate-900">Order Details</span>
            </div>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="bg-white rounded-2xl border border-slate-200 p-8" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }} data-testid="order-not-found-card">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              isDeletedFromNotification ? 'bg-amber-50' : 'bg-blue-50'
            }`}>
              <AlertCircle className={`w-8 h-8 ${isDeletedFromNotification ? 'text-amber-500' : 'text-blue-400'}`} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{headline}</h2>
            <p className="text-slate-600 mb-6">{bodyMsg}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button
                variant="outline"
                onClick={handleBack}
                className="text-slate-600 border-slate-200 hover:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {backLabel.replace(/^←\s*/, '')}
              </Button>
              {isDeletedFromNotification && (user?.role === 'admin' || user?.role === 'accounting') && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/billing?tab=delivery-exceptions')}
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-testid="open-exception-queue-btn"
                >
                  Open Exception Queue
                </Button>
              )}
              {!isDeletedFromNotification && (
                <Button
                  onClick={handleRetry}
                  className="bg-blue-600 hover:bg-blue-700 text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main layout with tabs - Applying Blue & White Theme Polish
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky Subheader - Always visible while scrolling (TITLEFLOW-ORDER-HEADER-UI-001 premium) */}
      <div className="sticky top-0 z-10 odh-header" data-testid="order-detail-header">
        <div className="odh-inner">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <button 
              type="button"
              onClick={handleBack} 
              className="odh-back focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              data-testid="order-detail-back-link"
            >
              <ChevronLeft className="w-4 h-4 -ml-1" aria-hidden="true" />
              {backLabel.replace(/^←\s*/, '')}
            </button>
            <span className="odh-sep" aria-hidden="true">›</span>
            <span className="odh-title" title={`Order Details – ${orderData.order_number}`}>Order Details – <span className={['rush', 'urgent'].includes(orderData.priority?.toLowerCase()) ? 'text-red-600' : ''}>{orderData.order_number}</span></span>
            <span className="odh-product ml-1" title={orderData.client_product}>{orderData.client_product}</span>
          </div>
            
          {/* Right: Status, Priority, Product Chip, Reassign Button */}
          <div className="odh-right">
              {/* Accounting Read-Only pill — sets clear expectations in the sticky header */}
              {user?.role === 'accounting' && (
                <span
                  className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-indigo-200"
                  title="Accounting role has view-only access. Editing is disabled."
                  data-testid="accounting-readonly-pill"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Read-only
                </span>
              )}
              {/* Quality-record linked access — processor is viewing an order
                  they no longer own because it's attached to one of their
                  quality records (My Quality Records → order link). */}
              {orderData?.quality_link_access && (
                <span
                  className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-200"
                  title="You can view this order because it is linked to one of your quality records. Editing is disabled."
                  data-testid="quality-link-readonly-pill"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Read-only · Quality record
                </span>
              )}
              {orderData?.status && <span className="odh-status" data-testid="order-detail-status"><StatusBadge status={orderData.status} userRole={user?.role} orderData={orderData} /></span>}
              {/* Sent-to recipient pill — May 4, 2026 (DELIVERY-UX).
                  When delivery has been finalized, surface the recipient
                  email right next to the Complete badge so wrong-email
                  accidents are catchable at a glance — no need to open
                  Fulfillment to verify. Visible to all roles. */}
              {(orderData?.status === 'complete' || orderData?.status === 'completed') &&
               orderData?.delivery_status === 'sent' &&
               orderData?.delivery_recipient && (
                <span
                  className="odh-pill odh-pill-sent bg-emerald-50 text-emerald-800 border border-emerald-200"
                  title={`Tax certificate sent to ${orderData.delivery_recipient}`}
                  data-testid="header-sent-to-pill"
                >
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-emerald-600 font-semibold">Sent to:</span>
                  <span className="odh-pill-email font-mono" data-testid="header-sent-to-email">{orderData.delivery_recipient}</span>
                </span>
              )}
              {/* Delivery Not Completed chip — Feb 25, 2026 P0.
                  When status=complete but delivery is not finalized, surface the
                  gap clearly so processors/admins know follow-up is required.
                  Visible to ALL roles (read-only consumers benefit from seeing it
                  too — Accounting now knows to check back later). */}
              {/* TYPING-DELIVERY-VERIFY-001 — three distinct delivery states so a
                  failed / pending-confirmation delivery can never read as delivered:
                  'failed' (red) · 'sending' (amber, awaiting SES confirmation) · other (amber generic).
                  A Typing-stage order whose last completion attempt failed delivery also shows the red chip. */}
              {((orderData?.status === 'complete' || orderData?.status === 'completed') &&
               !['sent', 'delivered', 'delivered_manually'].includes(orderData?.delivery_status)) ||
               (orderData?.status === 'typing' && orderData?.delivery_status === 'failed') ? (
                orderData?.delivery_status === 'failed' ? (
                  <span
                    className="odh-pill bg-rose-50 text-rose-700 border border-rose-300 max-w-[360px]"
                    title={orderData?.delivery_error || 'The final report could not be delivered to the client.'}
                    data-testid="delivery-failed-chip"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.34 16a2 2 0 001.73 3z" />
                    </svg>
                    <span className="truncate">Delivery Failed{orderData?.status === 'typing' ? ' · still in Typing' : ''}</span>
                  </span>
                ) : orderData?.delivery_status === 'sending' ? (
                  <span
                    className="odh-pill bg-amber-50 text-amber-800 border border-amber-300"
                    title="Completion recorded; the email service has not yet confirmed acceptance. Not delivered until this becomes 'Sent to'."
                    data-testid="delivery-pending-confirmation-chip"
                  >
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Delivery Pending Confirmation
                  </span>
                ) : (
                <span
                  className="odh-pill bg-amber-50 text-amber-800 border border-amber-300"
                  title={`Order is complete but delivery_status="${orderData?.delivery_status || 'unknown'}". Billing will only include this order once delivery is finalized.`}
                  data-testid="delivery-not-completed-chip"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.34 16a2 2 0 001.73 3z" />
                  </svg>
                  Delivery Not Completed
                </span>
                )
              ) : null}
              <ResendTaxCertificateButton orderId={orderData?.id} orderStatus={orderData?.status} userRole={user?.role} onSent={refreshOrderData} />
              {orderData?.priority && <PriorityIndicator priority={orderData.priority} />}
              <div className="odh-chip" title={`${orderData.product_id} • ${orderData.client_code}`} data-testid="order-detail-product-chip">
                {orderData.product_id} • {orderData.client_code}
              </div>
              {/* Reassign Button for Team Leaders / Admin */}
              {canReassign && orderData?.status !== 'complete' && orderData?.status !== 'cancelled' && (
                <button
                  type="button"
                  onClick={() => setShowReassignDialog(true)}
                  className="odh-reassign focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  data-testid="reassign-order-btn"
                >
                  <UserPlus aria-hidden="true" />
                  Reassign
                </button>
              )}

              {/* Mark Delivered — Feb 25, 2026 P0.
                  Restricted to Admin / Team Leader. Only visible when:
                    - Order is complete
                    - delivery_status is NOT already finalized
                  Backend POST /orders/{id}/mark-delivered enforces RBAC + idempotency. */}
              {(user?.role === 'admin' ||
                user?.role === 'team_leader' ||
                user?.role === 'tax_search_team_leader' ||
                user?.role === 'title_search_team_leader') &&
               (orderData?.status === 'complete' || orderData?.status === 'completed') &&
               !['sent', 'delivered', 'delivered_manually'].includes(orderData?.delivery_status) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMarkDeliveredDialog(true)}
                  className={`text-amber-700 border-amber-300 hover:bg-amber-50 transition-shadow ${
                    highlightMarkDelivered
                      ? 'ring-2 ring-amber-400 ring-offset-2 shadow-lg animate-pulse'
                      : ''
                  }`}
                  data-testid="mark-delivered-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Mark Delivered
                </Button>
              )}
          </div>
        </div>
      </div>

      {/* Order Desk Manual Linking (presentation; linking actions keep their own backend gates) */}
      {isOrderDeskView && (
        <ManualLinkingBanner orderData={orderData} />
      )}

      {/* Sticky Tab Navigation - premium white bar with icons (TITLEFLOW-ORDER-HEADER-UI-001) */}
      <div className="odh-tabs-wrap">
        <div className="odh-tabs scrollbar-hide" role="tablist" aria-label="Order details navigation">
            {tabs.map((tab) => {
              const TabIcon = ORDER_TAB_ICONS[tab.id];
              return (
              <button
                key={tab.id}
                id={`${tab.id}-tab`}
                data-testid={`order-tab-${tab.id}`}
                onClick={() => handleTabChange(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, tab.id)}
                className="odh-tab focus:outline-none"
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
                tabIndex={activeTab === tab.id ? 0 : -1}
              >
                {TabIcon && <TabIcon aria-hidden="true" />}
                {tab.label}
              </button>
              );
            })}
        </div>
      </div>

      {/* P0: Assignment Required Banner */}
      {isProcessingBlocked && (
        <div className="mx-6 mt-4" data-testid="assignment-required-banner">
          <div className="flex items-center justify-between gap-4 px-5 py-3.5 rounded-xl border border-amber-300/60 bg-amber-50" style={{ boxShadow: '0 2px 8px rgba(217,119,6,0.08)' }}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-4.5 h-4.5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-900">This order must be assigned before processing</p>
                <p className="text-xs text-amber-700 mt-0.5">Please assign a user to continue. All processing actions are disabled until assignment.</p>
              </div>
            </div>
            {canReassign && (
              <Button
                size="sm"
                onClick={handleAssignToMe}
                className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-4 py-2 rounded-lg"
                data-testid="assign-to-me-btn"
              >
                <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                Assign to Me
              </Button>
            )}
          </div>
        </div>
      )}

      {/* MLS-ASSIGN-3B: dual MLS component-assignment prompt (informational —
          no overlay, no parent Assign-to-Me; components are assigned
          individually via the summary strip or the order list). */}
      {isEnforcedRole && isDualFullyUnassigned && !['complete', 'cancelled'].includes(orderData?.status) && (
        <div className="mx-6 mt-4" data-testid="mls-components-unassigned-banner">
          <div className="flex items-center gap-3 px-5 py-3 rounded-xl border border-amber-300/60 bg-amber-50">
            <AlertCircle className="w-4.5 h-4.5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Assign the Tax Search and MLS Search components to begin processing.</span>
              <span className="text-xs text-amber-700 ml-2">Use the component Assign controls below or the order-list Assign action.</span>
            </p>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 bg-slate-50" style={isProcessingBlocked ? { position: 'relative' } : undefined}>
        {isProcessingBlocked && (
          <div 
            className="absolute inset-0 z-20 bg-slate-50/60 backdrop-blur-[1px] cursor-not-allowed"
            data-testid="processing-blocked-overlay"
            title="Assign this order before processing"
          />
        )}
        {activeTab === 'summary' && (
          <div id="summary-panel" role="tabpanel" aria-labelledby="summary-tab">
            <SummaryTab
              orderData={orderData}
              onOrderUpdate={(updatedOrder) => setOrderData(prev => ({ ...prev, ...updatedOrder }))}
              onRefreshOrder={refreshOrderData}
            />
          </div>
        )}
        {activeTab === 'overview' && (
          <div id="overview-panel" role="tabpanel" aria-labelledby="overview-tab">
            <OverviewTab 
              orderData={orderData} 
              effectiveRole={effectiveRole}
              onOrderUpdate={(updatedOrder) => {
                setOrderData(prev => ({ ...prev, ...updatedOrder }));
              }}
              onNavigateToTab={(tab) => setActiveTab(tab)}
            />
          </div>
        )}
        {activeTab === 'submissions' && (
          <div id="submissions-panel" role="tabpanel" aria-labelledby="submissions-tab">
            <SubmissionsTab orderData={orderData} />
          </div>
        )}
        {activeTab === 'fulfillment' && (
          <div id="fulfillment-panel" role="tabpanel" aria-labelledby="fulfillment-tab">
            <FulfillmentTab
              orderData={orderData}
              onOrderUpdate={(updatedOrder) => {
                setOrderData(prev => ({ ...prev, ...updatedOrder }));
              }}
              onRefreshOrder={refreshOrderData}
              onNavigateToTab={(tab) => setActiveTab(tab)}
            />
          </div>
        )}
        {activeTab === 'activity' && (
          <div id="activity-panel" role="tabpanel" aria-labelledby="activity-tab">
            <ActivityTab orderData={orderData} />
          </div>
        )}
        {activeTab === 'files' && (
          <div id="files-panel" role="tabpanel" aria-labelledby="files-tab">
            <FilesTab orderData={orderData} />
          </div>
        )}
      </div>

      {/* Order Reassignment Dialog - for Team Leaders and Admin */}
      {canReassign && (
        <OrderReassignmentDialog
          isOpen={showReassignDialog}
          onClose={() => setShowReassignDialog(false)}
          order={orderData}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          onSuccess={(result) => {
            // Update local order data with new assignee
            setOrderData(prev => ({
              ...prev,
              assignee_id: result.assignee_id,
              assignee_name: result.assignee_name,
              assignment_locked: result.assignment_locked
            }));
          }}
        />
      )}

      {/* Mark Delivered Modal — Feb 25, 2026 P0.
          Confirmation step + optional note + audit logging are all handled
          server-side by POST /orders/{id}/mark-delivered. */}
      {showMarkDeliveredDialog && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="mark-delivered-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Mark order as delivered</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Confirms the deliverable for{' '}
                  <span className="font-mono font-semibold">{orderData?.order_number}</span> reached
                  the customer (e.g. hand-delivered, manual upload, or auto-email failed).
                  This makes the order eligible for billing.
                </p>
              </div>
            </div>

            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Note <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={markDeliveredNote}
              onChange={(e) => setMarkDeliveredNote(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="e.g. Hand-delivered to client by Mike on 2/25, signed receipt attached."
              className="w-full text-sm border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
              data-testid="mark-delivered-note-input"
            />

            {markDeliveredError && (
              <div
                className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                data-testid="mark-delivered-error"
              >
                {markDeliveredError}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMarkDeliveredDialog(false);
                  setMarkDeliveredNote('');
                  setMarkDeliveredError(null);
                }}
                disabled={markDeliveredSubmitting}
                data-testid="mark-delivered-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkDelivered}
                disabled={markDeliveredSubmitting}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="mark-delivered-confirm"
              >
                {markDeliveredSubmitting ? 'Marking…' : 'Confirm — Mark Delivered'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Qualia-style Files Tab Component

// FilesTab is now imported from ./components/files/FilesTab

// File modal components are now imported from ./components/modals/FileModals

/**
 * Tax-only route guard for /team/auto-assignment.
 *
 * The Auto Assignment Dashboard is a Tax Team Queue Management page (the
 * trigger endpoint is hardcoded `POST /api/auto-assignment/auto-assign/tax-team/run`
 * and the page title literally says "Tax Team Queue Management"). There is no
 * Title-team backend workflow today, so Title Search Team Leaders are
 * redirected back to their dashboard rather than landing on an empty /
 * misleading page. Admin retains access; Tax TL retains access.
 *
 * Added Jun 1, 2026 alongside the corresponding RBAC allow-list cleanup.
 */
const AutoAssignGuard = ({ children }) => {
  // Phase A.6 (Jun 6, 2026) — Title TL is no longer redirected away.
  // The dashboard component now renders Tax/Search tabs and pre-selects
  // the right team based on the user's role.
  return children;
};


// Protected Route Component with defensive auth checking and browser history support
const ProtectedRoute = ({ children }) => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? true;
  const location = useLocation();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(false);
  const [authValid, setAuthValid] = useState(null);
  // RBAC-PILOT-ORDERS-001: matrix grants may open /dashboard/orders for JR
  const { hasPermission: hasMatrixPermission, loading: matrixLoading } = usePermissions();
  
  // Validate token exists and is not expired
  const validateToken = useCallback(() => {
    const token = safeLocalStorage.get('token');
    
    if (!token) {
      console.log('[ProtectedRoute] No token found');
      return false;
    }
    
    try {
      // Decode token and check expiration
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log('[ProtectedRoute] Invalid token format');
        return false;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      
      if (!exp) {
        console.log('[ProtectedRoute] Token has no expiration');
        return false;
      }
      
      // Check if expired (with 30 second buffer)
      const now = Math.floor(Date.now() / 1000);
      if (exp < now + 30) {
        console.log('[ProtectedRoute] Token expired or expiring soon');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('[ProtectedRoute] Token validation error:', error);
      return false;
    }
  }, []);
  
  // SECURITY FIX P0: Server-side token validation for critical operations
  const validateTokenWithServer = useCallback(async () => {
    const token = safeLocalStorage.get('token');
    if (!token) return false;
    
    try {
      const response = await dedupedGet(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 10000 // 10 second timeout
      });
      return !!response.data;
    } catch (error) {
      // CRITICAL FIX: Only return false (trigger logout) on actual 401 auth failure
      // Network errors, timeouts, etc. should NOT cause logout
      if (error.response?.status === 401) {
        console.log('[ProtectedRoute] Server validation failed: 401 Unauthorized');
        return false;
      }
      // For other errors (network, timeout, 500, etc.), assume token is still valid
      // This prevents logout loops during page transitions or temporary network issues
      console.log('[ProtectedRoute] Server validation error (non-401), assuming valid:', error.message);
      return true;
    }
  }, []);
  
  // Re-validate on every route change (including back/forward)
  useEffect(() => {
    const isValid = validateToken();
    setAuthValid(isValid);
    
    if (!isValid && !loading) {
      console.log('[ProtectedRoute] Auth invalid on route change, redirecting to login');
      // PREMIUM-NOTIFY-001 — tell the Login page why (client-side expiry/invalid token) and drop the cached profile
      try { queueLogoutNotice(safeLocalStorage.get('token') ? 'token_expired' : 'session_invalid'); } catch (_) { /* noop */ }
      // Clear any stale auth data
      safeLocalStorage.remove('token');
      safeLocalStorage.remove('refresh_token');
      safeLocalStorage.remove('user');
      delete axios.defaults.headers.common['Authorization'];
      
      // SECURITY FIX P0: Use window.location.replace to prevent history caching
      window.location.replace('/login');
    }
  }, [location.pathname, location.key, validateToken, loading, navigate, location]);
  
  // SECURITY FIX P0: Validate with server periodically and on page load
  useEffect(() => {
    // Validate with server on initial mount
    const serverValidate = async () => {
      const serverValid = await validateTokenWithServer();
      if (!serverValid && !loading) {
        console.log('[ProtectedRoute] Server validation failed on mount');
        try { queueLogoutNotice('session_invalid'); } catch (_) { /* noop */ }
        safeLocalStorage.remove('token');
        safeLocalStorage.remove('refresh_token');
        safeLocalStorage.remove('user');
        window.location.replace('/login');
      }
    };
    
    if (authValid) {
      serverValidate();
    }
  }, [authValid, validateTokenWithServer, loading]);
  
  // Listen for browser back/forward navigation (popstate)
  // P0 SECURITY REQUIREMENT: Back from protected route = LOGOUT
  // This is a strict security policy where ANY back navigation ends the session
  useEffect(() => {
    // Track if we're on a protected route
    const protectedPaths = ['/dashboard', '/orders', '/workspace', '/team', '/reports', '/analytics', '/admin', '/billing'];
    const isProtectedPath = protectedPaths.some(p => location.pathname.startsWith(p));
    
    if (!isProtectedPath) return;
    
    // P0 SECURITY: beforeunload - fires ONLY when page is actually being closed/navigated away
    // This is more reliable than pagehide for detecting real page unloads vs in-app navigation
    const handleBeforeUnload = (event) => {
      // Only clear auth if we're navigating away from the app entirely
      // NOT for in-app React Router navigation
      const isNavigatingToOurApp = document.activeElement?.tagName === 'A' && 
        document.activeElement?.href?.includes(window.location.hostname);
      
      // Skip clearing for in-app link clicks (those go through React Router)
      if (isNavigatingToOurApp) {
        console.log('[ProtectedRoute] In-app navigation, keeping auth');
        return;
      }
      
      console.log('[ProtectedRoute] beforeunload - potential page exit');
    };
    
    // P0 SECURITY: pagehide - fires when page is stored in bfcache (back button, close)
    // Feb 2026 (presence-leak fix): ALWAYS fire the logout beacon on
    // pagehide — regardless of `event.persisted`. Previous behaviour
    // bailed when `!persisted`, which covers tab close, window close,
    // navigation off-domain, and crashes — i.e. exactly the cases
    // where the user never sent any logout signal and stayed visible
    // in the Activity panel forever. The backend stale-session
    // sweeper is the second line of defence (~25 min later); this
    // beacon closes the row immediately.
    const handlePageHide = (event) => {
      const tokenForLogout = safeLocalStorage.get('token');
      if (!tokenForLogout) return;

      const beaconUrl = `${API}/auth-v2/logout/beacon`;
      const payload = JSON.stringify({
        token:  tokenForLogout,
        source: event.persisted ? 'visibilitychange' : 'pagehide',
      });

      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            beaconUrl,
            new Blob([payload], { type: 'application/json' })
          );
        } else {
          // Older browsers without sendBeacon — fetch keepalive
          fetch(beaconUrl, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: payload,
          }).catch(() => {});
        }
      } catch (_) {
        // Beacon failures are silent by design — the backend sweeper
        // will catch this row within ~25 min.
      }

      // ONLY clear local auth for bfcache persistence (back-button
      // scenario). For real tab close, the document is being torn
      // down anyway, so clearing storage just slows the unload.
      if (event.persisted) {
        safeLocalStorage.remove('token');
        safeLocalStorage.remove('refresh_token');
        safeLocalStorage.remove('user');
        sessionStorage.clear();
        delete axios.defaults.headers.common['Authorization'];
      }
    };
    
    // P0 SECURITY: pageshow - when page is restored, verify auth
    const handlePageShow = (event) => {
      if (event.persisted) {
        console.log('[ProtectedRoute] Page restored from bfcache');
        const token = safeLocalStorage.get('token');
        if (!token) {
          console.log('[ProtectedRoute] No token after bfcache restore, redirecting');
          window.location.replace('/login');
        }
      }
    };
    
    // P0 SECURITY: popstate - back/forward navigation
    const handlePopState = (event) => {
      console.log('[ProtectedRoute] popstate detected');
      const token = safeLocalStorage.get('token');
      if (!token) {
        console.log('[ProtectedRoute] No token after popstate, redirecting');
        window.location.replace('/login');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname]);
  
  // Also validate on visibility change (tab focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const isValid = validateToken();
        if (!isValid && !loading) {
          console.log('[ProtectedRoute] Auth invalid on tab focus, redirecting');
          window.location.replace('/login');
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [validateToken, loading]);
  
  // Show loading while auth is being validated
  if (loading || authValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying session...</p>
        </div>
      </div>
    );
  }
  
  // Final check - must have both user context and valid token
  if (!user || !authValid) {
    // SECURITY FIX: Don't pass 'from' state - always redirect to dashboard after login
    return <Navigate to="/login" replace />;
  }

  // Session 24 (Feb 22, 2026) — role gate:
  // `jurisdiction_requestor` must NEVER see the operational order dashboard,
  // order lists, or order-detail routes. These routes are all wrapped in
  // this ProtectedRoute (non-admin routes); the AdminLayout tree has its
  // own AdminProtectedRoute that already allow-lists the role.
  //
  // Feb 25, 2026 — defense-in-depth: detect requestor across `role`,
  // `roles[]`, and `role_ids[]` so legacy/cached/partial user records
  // can't bypass this gate.
  //
  // May 17, 2026 — `/dashboard/workspace` is the single exception per
  // My Workspace RBAC update. Jurisdiction Requestor keeps their portal
  // landing but is allowed to navigate to the personal workspace.
  if (isJurisdictionRequestor(user) && location.pathname !== '/dashboard/workspace') {
    // RBAC-PILOT-ORDERS-001: /dashboard/orders opens when the matrix grants orders:view
    const access = jrOrdersAccess(location.pathname, hasMatrixPermission, matrixLoading);
    if (access === 'pending') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="jr-permissions-loader">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      );
    }
    if (access === 'redirect') {
      return <Navigate to="/admin/my-jurisdiction-dashboard" replace />;
    }
  }

  return children;
};

// Root Path Redirect Component - handles "/" path
// Redirects to dashboard if authenticated, login if not
const RootRedirect = () => {
  const token = safeLocalStorage.get('token');
  
  if (token) {
    // Validate token is not expired
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const exp = payload.exp;
        const now = Math.floor(Date.now() / 1000);
        if (exp && exp > now) {
          // Session 24 — route jurisdiction_requestor to the jurisdiction
          // area; the main `/dashboard` is forbidden for this role.
          // Feb 25, 2026 — defense-in-depth across role/roles/role_ids.
          try {
            const userData = JSON.parse(safeLocalStorage.get('user') || '{}');
            // WORKSPACE-SWITCHER-001: keep the workspace chosen this session
            if (isJurisdictionRequestor(userData) && getActiveWorkspace() === 'order_desk') {
              return <Navigate to="/dashboard/orders" replace />;
            }
            if (isJurisdictionRequestor(userData)) {
              return <Navigate to="/admin/my-jurisdiction-dashboard" replace />;
            }
          } catch (_) { /* fall through */ }
          return <Navigate to="/dashboard" replace />;
        }
      }
    } catch (e) {
      // Invalid token
    }
  }
  
  // No valid token - redirect to login
  safeLocalStorage.remove('token');
  safeLocalStorage.remove('refresh_token');
  return <Navigate to="/login" replace />;
};

// NotFound Redirect Component - Prevents blank screens
// Redirects to dashboard if authenticated, login if not
const NotFoundRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    console.log('[NotFoundRedirect] Unknown route accessed:', location.pathname);
    
    // Check if user has valid token
    const token = safeLocalStorage.get('token');
    let isAuthenticated = false;
    
    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const exp = payload.exp;
          const now = Math.floor(Date.now() / 1000);
          isAuthenticated = exp && exp > now;
        }
      } catch (e) {
        console.error('[NotFoundRedirect] Token validation error:', e);
      }
    }
    
    if (isAuthenticated) {
      // Session 24 — jurisdiction_requestor cannot see the order dashboard.
      // Feb 25, 2026 — defense-in-depth across role/roles/role_ids.
      let userData = null;
      try {
        userData = JSON.parse(safeLocalStorage.get('user') || '{}');
      } catch (_) { /* ignore */ }
      if (isJurisdictionRequestor(userData)) {
        console.log('[NotFoundRedirect] jurisdiction_requestor, redirecting to /admin/my-jurisdiction-dashboard');
        navigate('/admin/my-jurisdiction-dashboard', { replace: true });
      } else {
        console.log('[NotFoundRedirect] User authenticated, redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
    } else {
      console.log('[NotFoundRedirect] User not authenticated, redirecting to login');
      // Clear any stale tokens
      safeLocalStorage.remove('token');
      safeLocalStorage.remove('refresh_token');
      navigate('/login', { replace: true });
    }
  }, [navigate, location.pathname]);
  
  // Show a brief loading state while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Redirecting...</p>
      </div>
    </div>
  );
};

// Main App Component with defensive programming
/** Global footer — adapts styling based on current route (dark on login, light elsewhere) */
const AppFooter = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/reset-password' || location.pathname === '/verify' || location.pathname === '/verify-email';
  
  // Hide global footer on login/auth pages — they have their own embedded footer
  if (isLoginPage) return null;
  
  return (
    <footer
      className="mt-auto bg-slate-50 border-t border-slate-200"
      data-testid="global-footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span>&copy; 2026 PNA Title Services LLC. All rights reserved.</span>
          <nav className="flex items-center gap-1.5 flex-wrap justify-center" data-testid="footer-nav">
            <a href="/privacy-policy" className="hover:text-slate-700 transition-colors">Privacy Policy</a>
            <span className="text-slate-300">|</span>
            <a href="/terms-of-use" className="hover:text-slate-700 transition-colors">Terms of Use</a>
            <span className="text-slate-300">|</span>
            <a href="/security" className="hover:text-slate-700 transition-colors">Security Notice</a>
            <span className="text-slate-300">|</span>
            <a href="/support" className="hover:text-slate-700 transition-colors">Contact Support</a>
          </nav>
        </div>
      </div>
    </footer>
  );
};

/** Simple placeholder page for footer links (Privacy, Terms, Security, Support) */
const FooterPage = ({ title }) => (
  <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="footer-page">
    <div className="text-center max-w-md">
      <h1 className="text-2xl font-semibold text-slate-800 mb-3">{title}</h1>
      <p className="text-slate-500 text-sm leading-relaxed">
        This page is coming soon. Please check back later or contact us at{' '}
        <a href="mailto:support@pnatitleservicesllc.com" className="text-blue-600 hover:underline">
          support@pnatitleservicesllc.com
        </a>
      </p>
      <a href="/" className="inline-block mt-6 text-sm text-blue-600 hover:text-blue-700 hover:underline">
        &larr; Back to Home
      </a>
    </div>
  </div>
);

function App() {
  const [hasFeatureChecksRun, setHasFeatureChecksRun] = useState(false);
  
  // Initialize feature checks on app start
  useEffect(() => {
    if (API && !hasFeatureChecksRun) {
      initializeFeatureChecks(API).then(() => {
        setHasFeatureChecksRun(true);
      }).catch((error) => {
        console.warn('Feature checks failed:', error);
        setHasFeatureChecksRun(true);
      });
    } else if (!API) {
      console.warn('API URL not configured, features may be limited');
      setHasFeatureChecksRun(true);
    }
  }, [hasFeatureChecksRun]);
  
  const sessionMonitor = useSessionMonitor({
    onTimeout: () => {
      // Handle logout with safe localStorage
      safeLocalStorage.remove('token');
      delete axios.defaults.headers.common['Authorization'];
      if (isBrowser) {
        window.location.replace('/login');
      }
    }
  });

  // Check for unsaved changes across the app
  const hasUnsavedChanges = false; // This would be managed by a global state if needed

  return (
    <ErrorBoundary>
      <div className="App">
        <AuthProvider>
          <StepUpAuthProvider>
            <BrowserRouter>
              <IdleManager>
                <GlobalNotificationBanner />
                <AnnouncementsBanner />
                <ShiftChangeAcknowledgeModal />
                <CelebrationOverlay />
                <BirthdayWishesPanel />
                <ForcePasswordChangeGate />
                <WorkspaceShellGate>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  {/* WORKSPACE-SWITCHER-001 — post-login chooser (2+ workspaces only) */}
                  <Route path="/choose-workspace" element={<ProtectedRoute><WorkspaceChooser /></ProtectedRoute>} />
                  {/* PROBATION-REVIEW-001 P3 — Reporting Manager's own reviews (any role; server-scoped) */}
                  <Route path="/probation-reviews" element={<ProtectedRoute><MyProbationReviews /></ProtectedRoute>} />
                  <Route path="/verify" element={<VerifyAccount />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  
                  {/* Public Footer Pages */}
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                  <Route path="/terms-of-use" element={<FooterPage title="Terms of Use" />} />
                  <Route path="/security" element={<FooterPage title="Security Notice" />} />
                  <Route path="/support" element={<FooterPage title="Contact Support" />} />
                  
                  {/* Dashboard Routes - Main app views with URL-based navigation */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <HRDashboardGuard><Dashboard /></HRDashboardGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/orders" element={
                    <ProtectedRoute>
                      <HRDashboardGuard><Dashboard /></HRDashboardGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/workspace" element={
                    <ProtectedRoute>
                      <HRDashboardGuard><Dashboard /></HRDashboardGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/team" element={
                    <ProtectedRoute>
                      <TeamTabRedirect><TeamPortalGuard><HRDashboardGuard><Dashboard /></HRDashboardGuard></TeamPortalGuard></TeamTabRedirect>
                    </ProtectedRoute>
                  } />
                  <Route path="/team/edit-requests" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/reports" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/favorites" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/processor-orders" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/typing" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  
                  {/* Legacy route redirects for backward compatibility */}
                  <Route path="/dashboard/leave" element={<Navigate to="/dashboard/workspace" replace />} />
                <Route path="/dashboard/attendance" element={<Navigate to="/dashboard/workspace" replace />} />
                
                <Route path="/orders/:orderId/*" element={
                  <ProtectedRoute>
                    <OrderDetailsLayout />
                  </ProtectedRoute>
                } />
                {/* QUALITY-PHASE3 — dedicated processor self-service page */}
                <Route path="/my-quality-records" element={
                  <ProtectedRoute>
                    <MyQualityRecordsPage />
                  </ProtectedRoute>
                } />
                {/* TaxCallbackSheet route removed - feature abandoned */}

                {/* TRAINING-WB-001 — ephemeral trainer whiteboard (admin / hr_manager / it_manager / TLs) */}
                <Route path="/training/whiteboard" element={
                  <TrainingProtectedRoute>
                    <TrainingWhiteboard />
                  </TrainingProtectedRoute>
                } />

                {/* PR 1 (Phase B1) — Break Management (admin + TL + manager) */}
                {/* CRM Phase 1 — isolated module: one persistent layout route, own RBAC gate. */}
                <Route path="/crm" element={<CrmProtectedRoute><CrmLayout /></CrmProtectedRoute>}>
                  <Route index element={<CrmHomePage />} />
                  <Route path="leads" element={<CrmLeadsPage />} />
                  <Route path="leads/:leadId" element={<CrmLeadDetailPage />} />
                  <Route path="follow-up/:activityId" element={<CrmFollowUpWorkspacePage />} />
                  <Route path="companies" element={<CrmCompaniesPage />} />
                  <Route path="companies/:companyId" element={<CrmCompanyDetailPage />} />
                  <Route path="opportunities" element={<CrmOpportunitiesPage />} />
                  <Route path="activity" element={<CrmActivityPage />} />
                  <Route path="performance" element={<CrmMyPerformancePage />} />
                  <Route path="workspace" element={<CrmMyWorkspacePage />} />
                </Route>
                {/* BM-007: ONE persistent layout route (shared header + shell stay
                    mounted); only the <Outlet /> page swaps between tabs. */}
                <Route path="/hr/breaks" element={
                  <BreakManagementProtectedRoute>
                    <BreakManagementLayout />
                  </BreakManagementProtectedRoute>
                }>
                  <Route index element={<Navigate to="/hr/breaks/history" replace />} />
                  <Route path="history" element={<BreakHistoryPage />} />
                  <Route path="active" element={<ActiveBreaksPage />} />
                  <Route path="flagged" element={<FlaggedBreaksPage />} />
                  <Route path="inactivity" element={<InactivityReportPage />} />
                </Route>
                
                {/* Admin UI Routes (PR A6) - Admin-only system configuration */}
                <Route path="/admin" element={
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                }>
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="crm" element={<CrmAdminOnly><CrmTeamActivityPage /></CrmAdminOnly>} />
                  <Route path="crm/performance" element={<CrmAdminOnly><CrmSalesPerformancePage /></CrmAdminOnly>} />
                  <Route path="roles" element={<AdminRoles />} />
                  <Route path="audit-logs" element={<AdminAuditLogs />} />
                  <Route path="hr/employees" element={<HREmployeeDirectory />} />
                  <Route path="hr/employees/new" element={<EmployeeWorkspace />} />
                  <Route path="hr/employees/:employeeId/edit" element={<EmployeeWorkspace />} />
                  <Route path="hr/employees/:employeeId" element={<EmployeeProfile />} />
                  <Route path="hr/audit-log" element={<HRAuditLog />} />
                  <Route path="hr/celebrations" element={<CelebrationSettings />} />
                  <Route path="recruitment/jobs" element={<JobOpenings />} />
                  <Route path="recruitment/candidates" element={<Candidates />} />
                  <Route path="recruitment/kanban" element={<RecruitmentKanban />} />
                  <Route path="client-codes" element={<ClientCodeManagement />} />
                  <Route path="contract-mappings" element={<ContractMappingAdmin token={localStorage.getItem('token')} />} />
                  <Route path="jurisdictions" element={<JurisdictionDirectory />} />
                  <Route path="jurisdictions/health" element={<JurisdictionHealthReport />} />
                  <Route path="jurisdictions/dashboard" element={<JurisdictionManagerDashboard />} />
                  {/* Feb 2026 — relocated from /admin/tax-collectors etc into
                      the Jurisdiction workflow. Now reachable by admin,
                      jurisdiction_manager, AND jurisdiction_requestor through
                      AdminLayout. Per-page role gates still enforce that only
                      admin / jurisdiction_manager can write. */}
                  <Route path="jurisdictions/tax-collectors" element={<TaxCollectorsPage />} />
                  <Route path="jurisdictions/tax-categories" element={<TaxCategoriesPage />} />
                  <Route path="jurisdictions/collector-corrections" element={<Navigate to="/admin/jurisdiction-requests?category=corrections" replace />} />
                  <Route path="jurisdictions/legacy-fallbacks" element={<LegacyFallbackQueuePage />} />
                  <Route path="jurisdiction-requests" element={<JurisdictionRequestsPage />} />
                  <Route path="mls-service-requests" element={<Navigate to="/admin/jurisdiction-requests" replace />} />
                  <Route path="my-jurisdiction-dashboard" element={<JurisdictionRequestorDashboard />} />
                  <Route path="settings/email" element={<AdminEmailSettings />} />
                  <Route path="settings/billing" element={<AdminBillingSettings />} />
                  <Route path="settings/session" element={<AdminSessionSettings />} />
                  <Route path="settings/qc" element={<AdminQCSettings />} />
                  <Route path="settings/quick-chips" element={<AdminQuickChipsSettings />} />
                  <Route path="settings/team" element={<AdminTeamManagement />} />
                  {/* SEC-DATAWIPE-001 (Jul 2026): Data Wipe page removed from
                      the web UI entirely. Direct URL access falls through to
                      the NotFoundRedirect catch-all. Backend endpoints are
                      env-gated (ENABLE_DATA_WIPE_ENDPOINTS) for emergency
                      maintenance only. */}
                  {/* IT Operations Dashboard - IT roles only */}
                  <Route path="it-dashboard" element={<ITDashboard />} />
                  {/* My Workspace (embedded) — May 17, 2026. Keeps IT /
                      Jurisdiction roles inside their dark portal shell when
                      they open My Workspace, instead of kicking them out to
                      the light Dashboard. */}
                  <Route path="workspace" element={<MyWorkspaceEmbedded />} />
                  {/* TEAM-SHELL-001: Team Management inside the role's own
                      portal shell (dark IT Ops for it_manager, HR Portal
                      for hr_manager). */}
                  <Route path="team" element={<TeamManagementEmbedded />} />
                  <Route path="hr/dashboard" element={<HREmployeeDirectory view="dashboard" />} />
                  <Route path="hr/leave-registry" element={<LeaveRegistry />} />
                  {/* HR-ANALYTICS-SCOPE-001 (Aug 25, 2026): Attendance Reports
                      rendered inside the HR/Admin Portal shell for HR roles */}
                  <Route path="hr/attendance-reports" element={<AttendanceReports />} />
                  {/* Jul 2026 — Product Master & Client Pricing nested under
                      AdminLayout so they render inside the standard Admin
                      Portal shell (sidebar + header) like every other admin
                      page. Accounting reaches them via the accounting
                      allow-list in AdminProtectedRoute; page/backend checks
                      keep write access scoped. */}
                  <Route path="products" element={<ProductManagement />} />
                  <Route path="pricing" element={<ClientSellingPricesPage />} />
                  <Route path="pricing/diagnostics" element={<PricingDiagnosticsPage />} />
                  <Route path="online-coverage" element={<OnlineCoveragePage />} />
                  <Route path="ground-rates" element={<GroundRateCardsPage />} />
                  <Route path="import-history" element={<ImportHistoryPage />} />
                  {/* Policy Center nested under AdminLayout (Jul 2026) for the
                      standard Admin Portal shell. Admin + HR Manager pass the
                      shell gate; PolicyManagement enforces POLICY_ADMIN_ROLES
                      itself for other HR roles. */}
                  <Route path="policies" element={<PolicyManagement />} />
                </Route>
                
                {/* Auto Assignment Dashboard — Tax-team-only feature.
                    Admin + Tax TL only. Title TL is redirected away to their
                    dashboard (Tax Team Queue Management has no Title-team
                    equivalent backend; would show an empty/misleading page). */}
                <Route path="/team/auto-assignment" element={
                  <ProtectedRoute>
                    <AutoAssignGuard>
                      <AutoAssignmentDashboard />
                    </AutoAssignGuard>
                  </ProtectedRoute>
                } />
                
                {/* QC Analytics — moved into the Analytics module (Aug 2026).
                    Old standalone URL redirects for bookmarks/UAT links. */}
                <Route path="/qc-analytics" element={<Navigate to="/analytics/qc" replace />} />
                
                {/* QC Dashboard - Dedicated QC Specialist Dashboard */}
                <Route path="/qc-dashboard" element={
                  <ProtectedRoute>
                    <QCDashboard />
                  </ProtectedRoute>
                } />

                {/* TYPING-001 Phase 2b — legacy path redirects into the
                    unified Dashboard shell for a consistent experience */}
                <Route path="/typing-dashboard" element={<Navigate to="/dashboard/typing" replace />} />
                
                {/* P1 QC Workspace - My assigned orders */}
                <Route path="/qc-workspace" element={
                  <ProtectedRoute>
                    <QCWorkspace />
                  </ProtectedRoute>
                } />
                
                {/* Analytics Routes - Accessible to Team Leaders + Admin (renamed from Reports, Jun 2026) */}
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <ReportsLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="/analytics/productivity" replace />} />
                  <Route path="attendance" element={<AttendanceReports hideHeader />} />
                  <Route path="downtime" element={<DowntimeAnalytics showHeader={false} />} />
                  <Route path="productivity" element={<ProductivityAnalytics />} />
                  <Route path="view-all" element={<AnalyticsListPage />} />
                  <Route path="targets" element={<PerformanceTargets />} />
                  <Route path="qc" element={<QCAnalyticsPage />} />
                </Route>
                
                {/* Policy Management (POLICY-MGMT-001) moved into the
                    AdminLayout nested routes above (Jul 2026) so it gets
                    the standard Admin Portal sidebar. */}

                {/* Legacy /reports/* and /admin/reports/* redirects — old bookmarks keep working */}
                <Route path="/reports/*" element={<LegacyReportsRedirect />} />
                <Route path="/reports" element={<LegacyReportsRedirect />} />
                <Route path="/admin/reports/*" element={<Navigate to="/analytics" replace />} />
                
                {/* Pricing Management & Product Management moved into the
                    AdminLayout nested routes above (Jul 2026) so they get
                    the standard Admin Portal sidebar. */}
                
                {/* Billing Dashboard - Accounting/Admin only.
                    Feb 2026: now renders inside <Dashboard /> so it
                    gets the same global header (logo, primary nav,
                    status indicator, downtime, notifications, user
                    profile) as every other in-app page. */}
                <Route path="/admin/billing" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />

                {/* Bulk Payment Upload — Feb 25, 2026 P0 */}
                <Route path="/admin/billing/bulk-payments" element={
                  <ProtectedRoute>
                    <BulkPaymentUpload />
                  </ProtectedRoute>
                } />

                {/* Phase 3 UI — Tax Collectors / Tax Categories / Collector
                    Corrections were originally top-level admin routes wired
                    into the order Dashboard. Feb 2026 — relocated under the
                    Jurisdiction Directory workflow (nested inside AdminLayout
                    at /admin/jurisdictions/*). These legacy paths now redirect
                    to the new locations so external bookmarks keep working. */}
                <Route path="/admin/tax-collectors" element={<Navigate to="/admin/jurisdictions/tax-collectors" replace />} />
                <Route path="/admin/tax-categories" element={<Navigate to="/admin/jurisdictions/tax-categories" replace />} />
                <Route path="/admin/collector-correction-requests" element={<Navigate to="/admin/jurisdictions/collector-corrections" replace />} />
                
                <Route path="/" element={<RootRedirect />} />
                
                {/* 404 Catch-all route - prevents blank screens */}
                <Route path="*" element={<NotFoundRedirect />} />
            </Routes>
                </WorkspaceShellGate>
                <AppFooter />
          </IdleManager>
            </BrowserRouter>
            <Toaster />
        
            {/* Session Timeout Modal */}
            <SessionTimeoutModal
              isOpen={sessionMonitor.showTimeoutModal}
              countdown={sessionMonitor.countdown}
              countdownFormatted={sessionMonitor.countdownFormatted}
              isRefreshingToken={sessionMonitor.isRefreshingToken}
              onStaySignedIn={sessionMonitor.staySignedIn}
              onSignOutNow={sessionMonitor.signOutNow}
              onClose={sessionMonitor.closeModal}
              isDirty={hasUnsavedChanges}
            />
          </StepUpAuthProvider>
        </AuthProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;